// @ts-nocheck
// Algolia PLP hooks: loaded only on collection and search templates.
// Depends on algolia-autocomplete.js (shared utilities) being loaded first.

// Native Back/Forward scroll restoration races Algolia InstantSearch rendering.
// Keep restoration manual and restore only for the PDP -> PLP return path below.
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

const PLP_SCROLL_RESTORE_KEY = 'ocs:plp-scroll-restore';
const PLP_SCROLL_RESTORE_TTL_MS = 30 * 60 * 1000;
const PLP_SCROLL_RESTORE_MAX_ATTEMPTS = 80;
const EQUIVALENT_HISTORY_PATCH = '__ocsEquivalentHistoryPatch';

/**
 * Replaces Algolia's presentation-only heading with a non-heading element.
 * The InstantSearch app injects this after the server-rendered collection title,
 * so leaving it as an h1 creates a duplicate heading in the rendered DOM.
 */
function demoteStrayAlgoliaHeadings(root) {
  if (!root) return 0;

  const headings = [];
  if (root.matches?.('h1.ais-h2')) headings.push(root);
  headings.push(...(root.querySelectorAll?.('h1.ais-h2') || []));

  for (const heading of headings) {
    const replacement = heading.ownerDocument.createElement('div');
    for (const attribute of heading.attributes) {
      replacement.setAttribute(attribute.name, attribute.value);
    }
    replacement.hidden = true;
    replacement.append(...heading.childNodes);
    heading.replaceWith(replacement);
  }

  return headings.length;
}

function initStrayAlgoliaHeadingGuard() {
  const root = document.querySelector(
    '.algolia-collection:not(.algolia-collection--search) #algolia-shopify-instantsearch',
  );
  if (!root) return;

  demoteStrayAlgoliaHeadings(root);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        demoteStrayAlgoliaHeadings(node);
      }
    }
  });
  observer.observe(root, { childList: true, subtree: true });
}

initStrayAlgoliaHeadingGuard();

function isProductUrl(url) {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin && parsed.pathname.includes('/products/');
  } catch (_) {
    return false;
  }
}

function getNavigationType() {
  const entries = performance.getEntriesByType?.('navigation') || [];
  return entries[0]?.type || '';
}

/**
 * Whether two URLs represent the same navigable state after normal URL parsing.
 * URLSearchParams decodes equivalent bracket/space encodings, and sorting the
 * entries makes query-string order irrelevant while retaining duplicate values.
 */
function urlsHaveEquivalentState(currentUrl, nextUrl) {
  let current;
  let next;

  try {
    current = new URL(currentUrl, window.location.href);
    next = new URL(nextUrl, current);
  } catch (_) {
    return false;
  }

  if (current.origin !== next.origin || current.pathname !== next.pathname || current.hash !== next.hash) {
    return false;
  }

  const customRouteParams = new Set(
    (window.algoliaCustomFilters?.filters || [])
      .map((filter) => filter.url_param)
      .filter(Boolean),
  );
  const decodeCustomRouteValue = (value) => value
    .split(',')
    .map((part) => {
      try {
        // URLSearchParams already decoded the URL once. Custom url_param state
        // performs this additional decode when it seeds filter selections, so
        // mirror that behavior when deciding whether two URLs mean the same thing.
        return decodeURIComponent(part.trim());
      } catch (_) {
        return part.trim();
      }
    })
    .join(',');
  const normalize = (url) => [...url.searchParams.entries()]
    .map(([key, value]) => [key, customRouteParams.has(key) ? decodeCustomRouteValue(value) : value])
    .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
      const keyOrder = leftKey.localeCompare(rightKey);
      return keyOrder || leftValue.localeCompare(rightValue);
    });

  return JSON.stringify(normalize(current)) === JSON.stringify(normalize(next));
}

/**
 * InstantSearch writes its canonical URL after a debounce. Install a durable
 * pushState guard rather than patching only for the synchronous router.write()
 * call, which finishes before that delayed history write occurs.
 */
function installEquivalentHistoryReplace() {
  if (window.history[EQUIVALENT_HISTORY_PATCH]) return false;

  const originalPushState = window.history.pushState;
  window.history.pushState = function (state, title, url) {
    if (urlsHaveEquivalentState(window.location.href, url)) {
      return window.history.replaceState(state, title, url);
    }
    return originalPushState.call(window.history, state, title, url);
  };
  window.history[EQUIVALENT_HISTORY_PATCH] = true;
  return true;
}

function isBackForwardRestore(event) {
  return Boolean(event?.persisted) || getNavigationType() === 'back_forward';
}

function savePlpScrollRestoreState(link) {
  if (!document.querySelector('.algolia-collection')) return;

  try {
    const productUrl = new URL(link.href, window.location.href);
    sessionStorage.setItem(
      PLP_SCROLL_RESTORE_KEY,
      JSON.stringify({
        url: window.location.href,
        productPath: productUrl.pathname,
        scrollX: Math.round(window.scrollX || 0),
        scrollY: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
        timestamp: Date.now(),
      }),
    );
  } catch (_) {}
}

function readPlpScrollRestoreState() {
  try {
    const state = JSON.parse(sessionStorage.getItem(PLP_SCROLL_RESTORE_KEY) || 'null');
    if (!state || state.url !== window.location.href) return null;
    if (Date.now() - state.timestamp > PLP_SCROLL_RESTORE_TTL_MS) {
      sessionStorage.removeItem(PLP_SCROLL_RESTORE_KEY);
      return null;
    }
    return state;
  } catch (_) {
    sessionStorage.removeItem(PLP_SCROLL_RESTORE_KEY);
    return null;
  }
}

function restorePlpScrollPosition(event) {
  if (!isBackForwardRestore(event)) return;

  const state = readPlpScrollRestoreState();
  if (!state) return;

  let attempts = 0;
  const run = () => {
    attempts += 1;
    const documentHeight = Math.max(
      document.body?.scrollHeight || 0,
      document.documentElement?.scrollHeight || 0,
    );
    const maxScrollY = Math.max(0, documentHeight - window.innerHeight);
    const targetY = Math.min(state.scrollY, maxScrollY);
    const hasHits = Boolean(document.querySelector('.ais-Hits-list [data-algolia-objectid], algolia-product-card'));
    const hasEnoughHeight = documentHeight >= state.scrollY + Math.min(window.innerHeight, 200);

    if ((hasHits && hasEnoughHeight) || attempts >= PLP_SCROLL_RESTORE_MAX_ATTEMPTS) {
      window.scrollTo(state.scrollX || 0, targetY);
      sessionStorage.removeItem(PLP_SCROLL_RESTORE_KEY);
      return;
    }

    window.setTimeout(run, attempts < 10 ? 50 : 100);
  };

  requestAnimationFrame(run);
}

function initPlpScrollRestore() {
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const link = target?.closest?.('a[href]');
      if (!link || (link.target && link.target !== '_self')) return;
      if (!isProductUrl(link.href)) return;
      savePlpScrollRestoreState(link);
    },
    true,
  );

  window.addEventListener('pageshow', restorePlpScrollPosition);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => restorePlpScrollPosition());
  } else {
    restorePlpScrollPosition();
  }
}

initPlpScrollRestore();

// =================================================================
// PLP ANALYTICS HELPERS
// =================================================================

function getPlpPageType() {
  return isSearchPage() ? 'search' : 'collection';
}

function getPlpCollectionHandle() {
  const match = window.location.pathname.match(/\/collections\/([^/?]+)/);
  return match ? match[1] : '';
}

function getSortPageType() {
  const pageType = getPlpPageType();
  if (pageType === 'search') return pageType;
  const collectionTitle = window.algoliaCustomFilters?.collection_title;
  return typeof collectionTitle === 'string' && collectionTitle.trim()
    ? collectionTitle.trim()
    : pageType;
}

function getSortEventSource() {
  return getPlpPageType() === 'search' ? 'search' : 'plp';
}

function findProductVariant(product, variantId) {
  return product?.variants?.find((variant) => String(variant.id) === String(variantId));
}

// Analytics item IDs come only from the endpoint matcher. When matcher output
// is absent the card fails closed with a blank item ID: the Shopify variant ID
// and the Algolia objectID are not valid analytics attribution identities.
// Commerce (add-to-cart) keeps using the real Shopify variant ID separately.
function setPlpCardItemId(card, variant) {
  if (!card || !variant) return;

  card.dataset.itemId = String(variant.item_id || '').trim();
}

/**
 * Replace the complete selected-variant analytics snapshot from endpoint data.
 * Product-level metadata and item name are populated separately at card render.
 *
 * @param {HTMLElement} card
 * @param {Object|null} variant
 * @returns {boolean}
 */
function syncPlpCardSelectedVariantAnalytics(card, variant) {
  if (!card?.dataset) return false;

  card.dataset.itemId = '';
  for (const key of [
    'itemVariant',
    'priceCents',
    'compareAtPriceCents',
    'variantAvailable',
    'variantStatus',
  ]) {
    delete card.dataset[key];
  }

  if (!variant || typeof variant !== 'object') return false;

  const itemIdIsValid =
    typeof variant.item_id === 'string' && variant.item_id.trim().length > 0;
  const itemVariantIsValid =
    typeof variant.option1 === 'string' && variant.option1.trim().length > 0;
  const priceIsValid =
    typeof variant.price === 'number' && Number.isFinite(variant.price) && variant.price > 0;
  const compareAtIsValid =
    variant.compare_at_price === null ||
    (
      typeof variant.compare_at_price === 'number' &&
      Number.isFinite(variant.compare_at_price) &&
      variant.compare_at_price >= 0
    );
  const availabilityIsValid = typeof variant.available === 'boolean';
  const statusIsValid =
    typeof variant.variant_status === 'string' &&
    variant.variant_status.trim().length > 0;

  if (
    !itemIdIsValid ||
    !itemVariantIsValid ||
    !priceIsValid ||
    !compareAtIsValid ||
    !availabilityIsValid ||
    !statusIsValid
  ) {
    return false;
  }

  setPlpCardItemId(card, variant);
  card.dataset.itemVariant = variant.option1;
  card.dataset.priceCents = String(variant.price);
  if (variant.compare_at_price !== null) {
    card.dataset.compareAtPriceCents = String(variant.compare_at_price);
  }
  card.dataset.variantAvailable = String(variant.available);
  card.dataset.variantStatus = variant.variant_status;
  return true;
}

function buildPlpInteractionPayload(details = {}) {
  const pageType = getPlpPageType();
  const payload = {
    event_source: 'plp',
    page_type: pageType,
    ...details,
  };

  const locale = document.documentElement.lang || window.Shopify?.locale || '';
  if (locale) payload.locale = locale;
  if (pageType === 'collection') {
    const collectionHandle = getPlpCollectionHandle();
    if (collectionHandle) payload.collection_handle = collectionHandle;
  }

  return payload;
}

function publishPlpAnalyticsEvent(name, payload) {
  if (typeof window.OCSAnalyticsEvents?.publishOcsAnalyticsEvent !== 'function') {
    return false;
  }

  try {
    return window.OCSAnalyticsEvents.publishOcsAnalyticsEvent(name, payload);
  } catch (error) {
    return false;
  }
}

// PLP-001/002/003: aside from the SRP content selector, the only plp_interaction
// controls are breadcrumbs and the In-Store Only local-retailer link, each with
// fixed English literals. This file loads on every template, so the breadcrumb
// branch stands down on a PDP (which owns its own pdp_interaction breadcrumb).
document.addEventListener('click', (event) => {
  if (!event.isTrusted) return;
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const breadcrumbLink = target.closest('.breadcrumbs a[href]');
  // Gate to PLP/search only: this file also loads on PDP (owns its own
  // pdp_interaction breadcrumb) and on blog/article templates that render the
  // same breadcrumbs section but are not plp_interaction surfaces.
  if (
    breadcrumbLink &&
    !document.querySelector('[data-ocs-pdp-root]') &&
    (isSearchPage() || /\/collections\//.test(window.location.pathname))
  ) {
    publishPlpAnalyticsEvent('ocs:plp_interaction', buildPlpInteractionPayload({
      interaction_type: 'Breadcrumbs',
      input_value: 'Category',
    }));
    return;
  }

  const retailerLink = target.closest('.ais-hit--in-store-message a[href]');
  const inStoreCta = target.closest('.ais-hit--cart-button__in-store[data-action="store-locator"]');
  if (retailerLink || inStoreCta) {
    publishPlpAnalyticsEvent('ocs:plp_interaction', buildPlpInteractionPayload({
      interaction_type: 'Store Locator',
      input_value: 'local retailer',
    }));
  }
  if (inStoreCta) {
    event.preventDefault();
    event.stopPropagation();
    window.location.assign(T.storeLocatorUrl());
  }
}, true);

// =================================================================
// CONSTANTS
// =================================================================

const SAVE_PERCENTAGE_THRESHOLD = window.themeSettings?.savePercentageThreshold || 5;

const SKELETON_FADE_MS = 400;
const CART_ERROR_RESET_MS = 2000;

const HITS_PER_PAGE_MOBILE = window.themeSettings?.hitsPerPageMobile || 30;
const HITS_PER_PAGE_DESKTOP = window.themeSettings?.hitsPerPageDesktop || 48;
// Query-level facet counting must be before distinct; dashboard afterDistinct can be overridden by requests.
const FACETING_AFTER_DISTINCT = false;

const AGE_GATE_PRODUCT_FILTER = 'tags:no_age-gate';
const AGE_GATE_CONTENT_FILTER = 'meta.auth.no_agegate:true';
const AGE_GATE_ARTICLE_FILTER = 'tags:no_age-gate';

function isAgeGateRestrictedSearch() {
  return Boolean(
    window.themeSettings?.searchAgeGateRestricted &&
    window.themeSettings?.ageGateRestrictedExperience,
  );
}

function appendAlgoliaFilter(existingFilter, filterToAppend) {
  if (!filterToAppend) return existingFilter || '';
  if (!existingFilter) return filterToAppend;
  if (existingFilter.includes(filterToAppend)) return existingFilter;
  return `(${existingFilter}) AND (${filterToAppend})`;
}

function getTemporaryLanguageFilter(sourceId) {
  return window.algoliaLanguageFilters?.filterForSource?.(sourceId) || '';
}

function filterTemporaryLanguageHits(sourceId, hits) {
  const filterHits = window.algoliaLanguageFilters?.filterHits;
  return typeof filterHits === 'function'
    ? filterHits(sourceId, hits)
    : (Array.isArray(hits) ? hits : []);
}

function isTemporaryLanguageSourceEnabled(sourceId) {
  return window.algoliaLanguageFilters?.isSourceEnabled?.(sourceId) !== false;
}

// Queue for variant fetching
const algoliaFetchQueue = [];
let algoliaFetchInProgress = false;

/** Extract Algolia collection filter string from the current URL */
function getCollectionFilter() {
  const pathParts = window.location.pathname.split("/");
  const idx = pathParts.indexOf("collections");
  if (idx !== -1 && pathParts[idx + 1] && pathParts[idx + 1] !== "all") {
    return `collections:${pathParts[idx + 1]}`;
  }
  return null;
}

// =================================================================
// PLP DATA & FORMATTING HELPERS
// =================================================================

/**
 * Build a locale-correct, absolute product URL from a hit.
 *
 * Why not algoliaShopify.helpers.instantsearchLink?
 * The Algolia CDN bundle's instantsearchLink (and its card-level click
 * handler) can return a *relative* path for non-default locales
 * (e.g. "fr/products/handle?variant=…" without a leading slash).
 * When the browser resolves that against the current collection URL
 * (/fr/collections/some-handle/) it produces the doubled-locale URL
 * /fr/collections/some-handle/fr/products/handle that the user reports.
 *
 * Using window.Shopify.routes.root (e.g. "/" or "/fr/") guarantees an
 * absolute path regardless of locale.
 */
function hitProductUrl(hit) {
  const root = window.Shopify?.routes?.root ?? '/';
  const addVariant = !hit._distinct && hit.objectID && hit.objectID !== hit.id;
  const qs = addVariant ? `?variant=${hit.objectID}` : '';
  return `${root}products/${hit.handle}${qs}`;
}

function getPlantType(hit) {
  const raw =
    hit.plant_type ||
    hit["meta.product.plant_type"] ||
    hit.meta?.product?.plant_type ||
    null;
  if (!raw) return null;
  return window.themeSettings?.plantTypes?.[raw] || raw;
}

function hitHasDiscount(hit) {
  return !!(
    hit.compare_at_price &&
    hit.compare_at_price > 0 &&
    hit.compare_at_price > hit.price
  );
}

function isAvailableForOrder(hit) {
  return (
    hit.can_order === true ||
    hit.inventory_available === true ||
    hit.available === true ||
    hit.inventory_quantity > 0
  );
}

function calculateSavePercentage(price, compareAtPrice) {
  if (!compareAtPrice || compareAtPrice <= price) return null;
  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

function getHitSavePercent(hit) {
  if (!hitHasDiscount(hit)) return null;
  const percent = calculateSavePercentage(hit.price, hit.compare_at_price);
  return percent && percent > SAVE_PERCENTAGE_THRESHOLD ? percent : null;
}

function getPricePerUnit(hit) {
  const ppu =
    hit.meta?.variant?.display_ppu ||
    hit["meta.variant.display_ppu"] ||
    hit.meta?.variant?.price_per_unit ||
    hit["meta.variant.price_per_unit"] ||
    hit.meta?.variant?.custom_pricing ||
    hit["meta.variant.custom_pricing"] ||
    null;
  if (!ppu) return null;
  const unit =
    hit.meta?.variant?.display_uom ||
    hit["meta.variant.display_uom"] ||
    hit.meta?.variant?.custom_uom ||
    hit["meta.variant.custom_uom"] ||
    hit.meta?.product?.content_uom_type ||
    hit["meta.product.content_uom_type"] ||
    hit.meta?.product?.unit_suffix ||
    hit["meta.product.unit_suffix"] ||
    "unit";
  return { ppu, unit };
}

function getCartSectionIds() {
  const sectionIds = new Set();
  document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
    sectionIds.add(el.dataset.sectionId);
  });

  return Array.from(sectionIds);
}

function parseVariantOptionSize(value) {
  const normalized = String(value || "").trim();
  const multipack = normalized.match(
    /^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i,
  );

  if (multipack) {
    return Number(multipack[1]) * Number(multipack[2]);
  }

  const quantity = normalized.match(/\d+(?:\.\d+)?/);
  return quantity ? Number(quantity[0]) : 0;
}

function compareVariantOptionValues(a, b) {
  return parseVariantOptionSize(a) - parseVariantOptionSize(b);
}

function sortVariantsSmallestToLargest(variants) {
  return [...variants].sort((a, b) =>
    compareVariantOptionValues(a.option1, b.option1),
  );
}

function selectBestVariant(product) {
  const orderedVariants = sortVariantsSmallestToLargest(product.variants);

  // Sort replicas can represent the same product with different variant-level
  // hits. Always derive the default from Shopify's complete variant list so
  // every PLP selects the smallest in-stock size regardless of sort order.
  return orderedVariants.find((variant) => variant.available) || orderedVariants[0];
}

// =================================================================
// PLP FORMATTING HELPERS
// =================================================================

function algoliaFormatPrice(price) {
  if (window.OCSMoney?.format) return window.OCSMoney.format(price);
  return `$${(price / 100).toFixed(2)}`;
}

function algoliaFormatPPU(price) {
  if (window.OCSMoney?.formatMajor) return window.OCSMoney.formatMajor(price);
  const amount = typeof price === "number" ? price : parseFloat(price) || 0;
  return `$${amount.toFixed(2)}`;
}

/**
 * Localize Algolia's preformatted hit price without assuming whether a given
 * index stores `price` in cents or major currency units. Shopify variant
 * responses use cents and continue through algoliaFormatPrice instead.
 */
function localizeAlgoliaDisplayPrice(displayPrice) {
  const value = String(displayPrice || '');
  const locale = window.Shopify?.locale || document.documentElement?.lang || 'en';
  if (!String(locale).toLowerCase().startsWith('fr') || !window.OCSMoney?.format) return value;

  return value.replace(/(?:CA)?\$\s*(\d[\d,]*(?:\.\d+)?)/g, (_match, amount) => {
    const majorUnits = Number(amount.replace(/,/g, ''));
    return Number.isFinite(majorUnits) ? window.OCSMoney.format(Math.round(majorUnits * 100)) : _match;
  });
}

function buildPriceHTML(price, compareAtPrice) {
  const hasDiscount = compareAtPrice && compareAtPrice > 0 && compareAtPrice > price;
  let html = `<b class="${hasDiscount ? "ais-hit--price-sale" : ""}">${algoliaFormatPrice(price)}</b>`;
  if (hasDiscount)
    html += ` <span class="ais-hit--price-striked">${algoliaFormatPrice(compareAtPrice)}</span>`;
  html += ` <span class="ais-hit--tax-label">${T.taxesIncluded()}</span>`;
  return html;
}

// =================================================================
// PLP HTML BUILDER HELPERS
// =================================================================

function buildCartButtonHTML(isAvailable, inStoreOnly = false) {
  if (inStoreOnly) {
    return `<button type="button" class="ocs-button ocs-button--solid-grey ais-hit--cart-button ais-hit--cart-button__in-store" data-action="store-locator" data-state="in-store" aria-label="${T.inStoreOnly()}"><span class="ais-hit--cart-button-text">${T.inStoreOnly()}</span></button>`;
  }
  if (isAvailable) {
    return `<button type="button" class="ocs-button ocs-button--primary ais-hit--cart-button" data-action="add-to-cart" aria-label="${T.addToCart()}"><span class="ais-hit--cart-button-text">${T.addToCart()}</span><span class="ais-hit--cart-button-spinner" data-spinner><svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.26" stroke-dashoffset="25.13" /></svg></span></button>`;
  }
  return `<button type="button" class="ocs-button ocs-button--solid-grey ais-hit--cart-button ais-hit--cart-button__oos" data-action="add-to-cart" data-state="oos" aria-label="${T.soldOut()}" disabled><span class="ais-hit--cart-button-text">${T.soldOut()}</span></button>`;
}

function ensureAlgoliaCartButtonSpinner(cartButton) {
  let spinner = cartButton.querySelector(".ais-hit--cart-button-spinner");
  if (!spinner) {
    cartButton.insertAdjacentHTML("beforeend", `<span class="ais-hit--cart-button-spinner" data-spinner><svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.26" stroke-dashoffset="25.13" /></svg></span>`);
    spinner = cartButton.querySelector(".ais-hit--cart-button-spinner");
  }
  if (spinner && !spinner.querySelector("svg")) {
    spinner.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.26" stroke-dashoffset="25.13" /></svg>`;
  }
}

function applyCartButtonState(cartButton, isAvailable, onClickFn, inStoreOnly = false) {
  cartButton.classList.remove(
    "ais-hit--cart-button__oos", "ais-hit--cart-button__in-store",
    "ocs-button--primary", "ocs-button--solid-grey"
  );
  cartButton.removeAttribute("data-state");

  if (!cartButton.querySelector(".ais-hit--cart-button-text")) {
    cartButton.innerHTML = `<span class="ais-hit--cart-button-text"></span>`;
  }

  const buttonText = cartButton.querySelector(".ais-hit--cart-button-text");

  if (inStoreOnly) {
    cartButton.disabled = false;
    cartButton.classList.add("ocs-button--solid-grey", "ais-hit--cart-button__in-store");
    if (buttonText) buttonText.textContent = T.inStoreOnly();
    cartButton.setAttribute("aria-label", T.inStoreOnly());
    cartButton.setAttribute("data-state", "in-store");
    cartButton.setAttribute("data-action", "store-locator");
    cartButton.onclick = null;
  } else if (isAvailable) {
    cartButton.setAttribute("data-action", "add-to-cart");
    ensureAlgoliaCartButtonSpinner(cartButton);
    cartButton.disabled = false;
    cartButton.classList.add("ocs-button--primary");
    if (buttonText) buttonText.textContent = T.addToCart();
    cartButton.setAttribute("aria-label", T.addToCart());
    cartButton.onclick = onClickFn
      ? function (event) {
          // PLP-001: quick add is an add-to-cart action, not a plp_interaction;
          // the enriched ocs:add_to_cart event carries the item metadata.
          return onClickFn.call(this, event);
        }
      : null;
  } else {
    cartButton.disabled = true;
    cartButton.setAttribute("data-action", "add-to-cart");
    cartButton.classList.add("ocs-button--solid-grey", "ais-hit--cart-button__oos");
    if (buttonText) buttonText.textContent = T.soldOut();
    cartButton.setAttribute("aria-label", T.soldOut());
    cartButton.setAttribute("data-state", "oos");
    cartButton.onclick = null;
  }
}

function isAcfToggleActive(urlParam, legacyLabelPattern) {
  const activeParams = window._acfActiveToggleParams;
  if (Array.isArray(activeParams)) return activeParams.includes(urlParam);
  return (window._acfActiveToggles || []).some(function (label) {
    return legacyLabelPattern.test(label);
  });
}

function buildSwatchesHTML(product, selectedVariant, productHandle, inStoreOnly = false) {
  const MAX_SWATCHES = 3;
  const optionValues = [...new Set(product.variants.map((v) => v.option1))];
  optionValues.sort(compareVariantOptionValues);

  const hideOos = isAcfToggleActive("hide_oos", /out.of.stock/i);
  const hideInStore = !isAcfToggleActive("show_in_store", /in.store/i);

  const filteredOptions = optionValues.filter(function (value) {
    const v = product.variants.find(function (v) { return v.option1 === value; });
    if (!v) return false;
    if (hideOos && !v.available) return false;
    if (hideInStore && (inStoreOnly || v.availability === "in-store")) return false;
    return true;
  });

  const visibleOptions = filteredOptions.slice(0, MAX_SWATCHES);
  const remainingCount = filteredOptions.length - MAX_SWATCHES;

  let html = visibleOptions.map((value) => {
    const variant = product.variants.find((v) => v.option1 === value);
    const isActive = variant && selectedVariant && variant.id === selectedVariant.id;
    const isAvailable = variant && variant.available;
    const variantIsInStore = inStoreOnly || variant?.availability === "in-store";
    const classes = [
      "ais-hit--swatch",
      isActive ? "ais-hit--swatch-active" : "",
      !isAvailable && !variantIsInStore ? "ais-hit--swatch-unavailable" : "",
      variantIsInStore ? "ais-hit--swatch-in-store" : "",
    ].filter(Boolean).join(" ");
    return `<button type="button" class="${classes}" data-action="select-variant" data-variant-id="${variant ? variant.id : ""}" data-variant-price="${variant ? variant.price : ""}" data-variant-compare-price="${variant ? variant.compare_at_price || "" : ""}" data-variant-available="${variant ? variant.available : false}" data-variant-in-store="${variantIsInStore}" data-variant-ppu="${variant ? variant.display_ppu || "" : ""}" data-variant-uom="${variant ? variant.display_uom || "" : ""}" aria-label="${value}" aria-pressed="${isActive ? "true" : "false"}">${value}</button>`;
  }).join("");

  if (remainingCount > 0) {
    const root = window.Shopify?.routes?.root ?? '/';
    html += `<a href="${root}products/${productHandle}" class="ais-hit--swatch-more">${T.moreOptions(remainingCount)}</a>`;
  }

  return html;
}

// =================================================================
// PLP RENDER FUNCTIONS (lit-html + HTML string pairs)
// =================================================================

function renderCannabinoidInfoHTML(hit) {
  const { thcMin, thcMax, cbdMin, cbdMax, unit, decimals } = getCannabinoidValuesWithFallback(hit);
  const thcRange = formatCannabinoidRange(thcMin, thcMax, unit, decimals);
  const cbdRange = formatCannabinoidRange(cbdMin, cbdMax, unit, decimals);
  if (!thcRange && !cbdRange) return "";
  let html = '<div class="ais-hit--cannabinoids">';
  if (thcRange) html += `<span class="ais-hit--thc">${T.thcLabel()}: ${thcRange}</span>`;
  if (cbdRange) html += `<span class="ais-hit--cbd">${T.cbdLabel()}: ${cbdRange}</span>`;
  html += "</div>";
  return html;
}

function renderPlantType(hit, html) {
  const plantType = getPlantType(hit);
  if (!plantType) return "";
  return html`<p class="ais-hit--plant-type">${plantType}</p>`;
}

function renderPlantTypeHTML(hit) {
  const plantType = getPlantType(hit);
  if (!plantType) return "";
  return `<p class="ais-hit--plant-type">${plantType}</p>`;
}

function renderBadgesHTML(hit) {
  const showNew = isNewProduct(hit);
  const showPriceDrop = hasPriceDrop(hit);
  if (!showNew && !showPriceDrop) return "";
  let html = `<div class="ais-hit--badges" role="list" aria-label="${T.productBadgesLabel()}">`;
  if (showNew) html += `<span class="ais-hit--badge ais-hit--badge-new" role="listitem">${T.newLabel()}</span>`;
  if (showPriceDrop) html += `<span class="ais-hit--badge ais-hit--badge-pricedrop" role="listitem">${T.discountLabel()}</span>`;
  html += "</div>";
  return html;
}

function renderOntarioGrownBadgeHTML(hit) {
  if (!isOntarioGrown(hit)) return "";
  const logoUrl = getOntarioGrownLogoUrl();
  return `<div class="ais-hit--ontario-grown"><img src="${logoUrl}" alt="${T.ontarioGrownAlt()}" title="${T.ontarioGrownTooltip()}" class="ais-hit--ontario-grown-img" /></div>`;
}

function renderPriceDisplay(hit, html) {
  if (isInStoreOnly(hit)) {
    return html`<div class="ais-hit--price-container ais-hit--price-container--in-store" data-price-container>
      <p class="ais-hit--in-store-message" dangerouslySetInnerHTML=${{ __html: T.inStoreContactRetailerHtml() }}></p>
    </div>`;
  }
  const hasDiscount = hitHasDiscount(hit);
  return html`
    <div class="ais-hit--price-container" data-price-container>
      <p class="ais-hit--price" data-price>
        <b class="${hasDiscount ? "ais-hit--price-sale" : ""}">${localizeAlgoliaDisplayPrice(algoliaShopify.helpers.displayPrice(hit, hit._distinct))}</b>
        ${hasDiscount ? html`<span class="ais-hit--price-striked">${localizeAlgoliaDisplayPrice(algoliaShopify.helpers.displayStrikedPrice(hit.price, hit.compare_at_price))}</span>` : ""}
        <span class="ais-hit--tax-label">${T.taxesIncluded()}</span>
      </p>
    </div>
  `;
}

function renderPriceDisplayHTML(hit) {
  if (isInStoreOnly(hit)) {
    return `<div class="ais-hit--price-container ais-hit--price-container--in-store" data-price-container><p class="ais-hit--in-store-message">${T.inStoreContactRetailerHtml()}</p></div>`;
  }
  const hasDiscount = hitHasDiscount(hit);
  const priceDisplay = localizeAlgoliaDisplayPrice(algoliaShopify.helpers.displayPrice(hit, hit._distinct));
  const comparePrice = hasDiscount
    ? localizeAlgoliaDisplayPrice(algoliaShopify.helpers.displayStrikedPrice(hit.price, hit.compare_at_price))
    : "";
  let html = '<div class="ais-hit--price-container" data-price-container><p class="ais-hit--price" data-price>';
  html += `<b class="${hasDiscount ? "ais-hit--price-sale" : ""}">${priceDisplay}</b>`;
  if (hasDiscount && comparePrice) html += `<span class="ais-hit--price-striked">${comparePrice}</span>`;
  html += `<span class="ais-hit--tax-label">${T.taxesIncluded()}</span></p></div>`;
  return html;
}

function renderSaveBadge(hit, html) {
  const savePercent = getHitSavePercent(hit);
  if (!savePercent) return "";
  return html`<span class="ais-hit--save-badge">${T.savePercent(savePercent)}</span>`;
}

function renderSaveBadgeHTML(hit) {
  const savePercent = getHitSavePercent(hit);
  if (!savePercent) return "";
  return `<span class="ais-hit--save-badge">${T.savePercent(savePercent)}</span>`;
}

function renderPricePerUnit(hit, html) {
  const ppuData = getPricePerUnit(hit);
  if (!ppuData) return "";
  return html`<p class="ais-hit--ppu">(${algoliaFormatPPU(ppuData.ppu)} / ${ppuData.unit})</p>`;
}

function renderPricePerUnitHTML(hit) {
  const ppuData = getPricePerUnit(hit);
  if (!ppuData) return "";
  return `<p class="ais-hit--ppu">(${algoliaFormatPPU(ppuData.ppu)} / ${ppuData.unit})</p>`;
}

function renderCartButton(hit, html) {
  if (isInStoreOnly(hit)) {
    return html`
      <button type="button" class="ocs-button ocs-button--solid-grey ais-hit--cart-button ais-hit--cart-button__in-store" data-action="store-locator" data-state="in-store" aria-label="${T.inStoreOnly()}">
        <span class="ais-hit--cart-button-text">${T.inStoreOnly()}</span>
      </button>
    `;
  }
  if (isAvailableForOrder(hit)) {
    return html`
      <button type="button" class="ocs-button ocs-button--primary ais-hit--cart-button" data-action="add-to-cart" aria-label="${T.addToCart()}">
        <span class="ais-hit--cart-button-text">${T.addToCart()}</span>
        <span class="ais-hit--cart-button-spinner" data-spinner>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.26" stroke-dashoffset="25.13" />
          </svg>
        </span>
      </button>
    `;
  }
  return html`
    <button type="button" class="ocs-button ocs-button--solid-grey ais-hit--cart-button ais-hit--cart-button__oos" data-action="add-to-cart" data-state="oos" aria-label="${T.soldOut()}" disabled>
      <span class="ais-hit--cart-button-text">${T.soldOut()}</span>
    </button>
  `;
}

function algoliaHitViewIndex(hit) {
  const productPosition = Number(hit?.productPosition);
  if (Number.isInteger(productPosition) && productPosition > 0) return productPosition;

  const fallbackPosition = Number(hit?._ocsViewIndex);
  return Number.isInteger(fallbackPosition) && fallbackPosition > 0
    ? fallbackPosition
    : 1;
}

// Root element of the classic InstantSearch card.
//
// data-price-drop and data-analytics-complete are conditional: a malformed
// compare-at price or an incomplete nested snapshot must leave them off the
// card entirely. The InstantSearch html tag interpolates attribute values, not
// attribute text, so each presence combination renders as its own element,
// mirroring renderCartButton's one-element-per-state shape.
function renderProductCardRoot(hit, analytics, children, html) {
  if (analytics.complete) {
    return html`
      <div
        class="ais-hit ais-product"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-ocs-item-index="${algoliaHitViewIndex(hit)}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-item-id=""
        data-item-name="${algoliaHitTitle(hit)}"
        data-item-variant=""
        data-price-cents=""
        data-compare-at-price-cents=""
        data-variant-available=""
        data-variant-status=""
        data-in-store="${isInStoreOnly(hit) ? "true" : "false"}"
        data-product-tags="${(hit.tags || hit._tags || []).join(',')}"
        data-item-brand="${analytics.brand}"
        data-item-category="${analytics.category}"
        data-item-category2="${analytics.category2}"
        data-item-category3="${analytics.category3}"
        data-ontario-grown="${analytics.ontarioGrown}"
        data-craft="${analytics.craft}"
        data-price-drop="${analytics.priceDrop}"
        data-analytics-complete="true"
      >${children}</div>
    `;
  }

  if (analytics.priceDrop) {
    return html`
      <div
        class="ais-hit ais-product"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-ocs-item-index="${algoliaHitViewIndex(hit)}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-item-id=""
        data-item-name="${algoliaHitTitle(hit)}"
        data-item-variant=""
        data-price-cents=""
        data-compare-at-price-cents=""
        data-variant-available=""
        data-variant-status=""
        data-in-store="${isInStoreOnly(hit) ? "true" : "false"}"
        data-product-tags="${(hit.tags || hit._tags || []).join(',')}"
        data-item-brand="${analytics.brand}"
        data-item-category="${analytics.category}"
        data-item-category2="${analytics.category2}"
        data-item-category3="${analytics.category3}"
        data-ontario-grown="${analytics.ontarioGrown}"
        data-craft="${analytics.craft}"
        data-price-drop="${analytics.priceDrop}"
      >${children}</div>
    `;
  }

  return html`
    <div
      class="ais-hit ais-product"
      data-algolia-index="${hit.index}"
      data-algolia-position="${hit.productPosition}"
      data-ocs-item-index="${algoliaHitViewIndex(hit)}"
      data-algolia-queryid="${hit.queryID}"
      data-algolia-objectid="${hit.objectID}"
      data-handle="${hit.handle}"
      data-variant-id="${hit.objectID}"
      data-item-id=""
      data-item-name="${algoliaHitTitle(hit)}"
      data-item-variant=""
      data-price-cents=""
      data-compare-at-price-cents=""
      data-variant-available=""
      data-variant-status=""
      data-in-store="${isInStoreOnly(hit) ? "true" : "false"}"
      data-product-tags="${(hit.tags || hit._tags || []).join(',')}"
      data-item-brand="${analytics.brand}"
      data-item-category="${analytics.category}"
      data-item-category2="${analytics.category2}"
      data-item-category3="${analytics.category3}"
      data-ontario-grown="${analytics.ontarioGrown}"
      data-craft="${analytics.craft}"
    >${children}</div>
  `;
}

// =================================================================

// SKELETON LOADING
// =================================================================

// Server-rendered skeleton (#acf-skeleton-page) is injected before .algolia-collection
// by an inline script in algolia-custom-filters.liquid, and overlays it via CSS.
// _acfRemoveSkeleton is set by that inline script; called here on first IS render.
window._removeCardSkeleton = function () {
  if (typeof window._acfRemoveSkeleton === "function") {
    window._acfRemoveSkeleton();
  } else {
    // Fallback: remove directly if inline script didn't run
    const skeleton = document.getElementById('acf-skeleton-page');
    if (skeleton) {
      skeleton.style.transition = 'opacity 0.4s ease';
      skeleton.style.opacity = '0';
      setTimeout(() => skeleton.remove(), SKELETON_FADE_MS);
    }
  }
  window._removeCardSkeleton = null;
};

// Safety net: if the skeleton is still visible after 8 seconds, force-remove it.
// Only runs on pages that actually have a skeleton (collection / search).
setTimeout(function () {
  if (typeof window._removeCardSkeleton === "function" && document.getElementById('acf-skeleton-page')) {
    console.warn('[Algolia] Skeleton timeout \u2014 forcing removal. Algolia may have failed to respond.');
    window._removeCardSkeleton();
  }
}, 8000);

// =================================================================

// SEARCH PAGE: FEDERATED SEARCH
// =================================================================

function isSearchPage() {
  return /\/search(\/|$|\?)/.test(window.location.pathname);
}

// Single source of truth for index-name construction.
// All call sites that need a primary or search-replica index name must go through
// these helpers so that the suffix strings ("products", "products_canada_fr") and
// the themeSettings.searchIndexes read live in exactly one place.
function resolvePrimaryIndex(prefix, isFR) {
  const suffix = isFR
    ? window.themeSettings?.primaryIndexes?.fr || "products_canada_fr"
    : window.themeSettings?.primaryIndexes?.en || "products";
  return prefix + suffix;
}

function resolveSearchReplicaIndex(prefix, isFR) {
  const configuredSuffix = isFR
    ? window.themeSettings?.searchIndexes?.fr
    : window.themeSettings?.searchIndexes?.en;
  return prefix + (configuredSuffix || (isFR ? "search_products_canada_fr" : "search_products"));
}

// Returns the effective index prefix, normalised to always end with "_".
// Theme setting takes priority over the app config value; if the theme setting
// is blank the app value is used as-is (already has trailing "_").
function resolveIndexPrefix() {
  const themeP = window.themeSettings?.algoliaIndexPrefix;
  const appP = window.algoliaShopify?.config?.index_prefix || "";
  const raw = (themeP && themeP.trim()) ? themeP.trim() : appP;
  return raw && !raw.endsWith("_") ? raw + "_" : raw;
}

/**
 * Resolve the index InstantSearch means to query into the fully-qualified theme
 * index. Manual clients (including Load More) bypass the InstantSearch client
 * rewrite, so they must use this resolver themselves.
 * @param {string} indexName
 * @returns {string}
 */
function resolveProductQueryIndex(indexName) {
  const isFR = window.Shopify?.locale?.startsWith("fr");
  const themePrefix = resolveIndexPrefix();
  const appPrefixRaw = window.algoliaShopify?.config?.index_prefix || "";
  const appPrefix = appPrefixRaw && !appPrefixRaw.endsWith("_")
    ? appPrefixRaw + "_"
    : appPrefixRaw;
  const appBase = appPrefix + (isFR ? "products_canada_fr" : "products");

  if (!indexName || indexName === appBase) {
    return isSearchPage()
      ? resolveSearchReplicaIndex(themePrefix, isFR)
      : resolvePrimaryIndex(themePrefix, isFR);
  }

  if (themePrefix && indexName.startsWith(themePrefix)) return indexName;

  if (appPrefix && indexName.startsWith(appPrefix)) {
    return themePrefix + indexName.slice(appPrefix.length);
  }

  // Legacy sortBy URLs can restore a bare product replica suffix into the
  // helper even though current theme-authored options are fully prefixed.
  if (indexName === "products" || indexName.startsWith("products_")) {
    return themePrefix + indexName;
  }

  return indexName;
}

let _secondarySearchClient = null;
let _secondaryQuery = '';
const _secondaryPagination = {
  pages:    { page: 0, nbPages: 1, loaded: 0, total: 0 },
  articles: { page: 0, nbPages: 1, loaded: 0, total: 0 },
};

const SECONDARY_HITS_PER_PAGE = 10;
// COMING_SOON_URL and hitImageUrl are declared in algolia-autocomplete.js,
// which is always loaded before this file.

function getSecondaryClient() {
  const config = window.algoliaShopify?.config;
  if (!config) { console.warn('[SecondarySearch] algoliaShopify.config not available'); return null; }
  const algoliasearch = window.algoliaShopify?.externals?.algoliasearch;
  if (!algoliasearch) { console.warn('[SecondarySearch] algoliasearch not available on externals'); return null; }
  if (!_secondarySearchClient) {
    _secondarySearchClient = algoliasearch(config.app_id, config.search_api_key || config.api_key);
  }
  return _secondarySearchClient;
}

async function searchFilteredSecondaryPage(index, type, query, searchParams, startPage = 0) {
  if (!isTemporaryLanguageSourceEnabled(type)) {
    return index.search(query, { ...searchParams, page: startPage });
  }

  let page = startPage;
  let result;

  do {
    result = await index.search(query, { ...searchParams, page });
    const hits = filterTemporaryLanguageHits(type, result.hits);
    if (hits.length || page >= result.nbPages - 1) {
      return { ...result, hits, page };
    }
    page += 1;
  } while (result && page < result.nbPages);

  return { ...(result || {}), hits: [], page };
}

async function getTemporaryEligibleHitCount(index, type, query, filters) {
  if (
    !isTemporaryLanguageSourceEnabled(type) ||
    (type !== 'pages' && type !== 'collections')
  ) {
    return null;
  }

  const attributesToRetrieve = type === 'pages'
    ? ['meta.locale.language']
    : ['handle'];
  const params = {
    hitsPerPage: 1000,
    attributesToRetrieve,
    attributesToHighlight: [],
    analytics: false,
    clickAnalytics: false,
  };
  if (filters) params.filters = filters;

  try {
    const result = await index.search(query, params);
    if (result.nbHits > result.hits.length) return null;
    return filterTemporaryLanguageHits(type, result.hits).length;
  } catch (error) {
    console.warn(`[SecondarySearch] ${type} language count failed:`, error);
    return null;
  }
}

function escapeSearchContentText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderContentHit(hit, type) {
  const title   = escapeSearchContentText(algoliaLocalizedContentTitle(hit));
  const url     = escapeSearchContentText(algoliaLocalizedContentUrl(hit, type));
  const raw     = hit.body_html_safe || '';
  const text    = raw.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  const excerpt = text.length > 250 ? text.slice(0, 250).replace(/\s\S+$/, '') + '…' : text;
  return `<article class="search-content-item">
    <a class="search-content-item__title" href="${url}">${title}</a>
    ${excerpt ? `<p class="search-content-item__excerpt search-content-item__excerpt--short">${excerpt}</p>` : ''}
    <a class="search-content-item__read-more" href="${url}">${T.readMore()}</a>
  </article>`;
}

function updateLoadMoreBtn(type) {
  const btn = document.querySelector(`.js-search-load-more--${type}`);
  if (!btn) return;
  const { page, nbPages, loaded, total } = _secondaryPagination[type];
  btn.style.display = loaded < total && page < nbPages - 1 ? 'block' : 'none';
}

async function loadMoreSecondary(type) {
  const client = getSecondaryClient();
  if (!client) return;
  const prefix = resolveIndexPrefix();
  const indexName = type === 'pages' ? prefix + 'pages' : prefix + 'articles';

  const btn = document.querySelector(`.js-search-load-more--${type}`);
  if (btn) { btn.disabled = true; btn.textContent = T.loading(); }

  try {
    const nextPage = _secondaryPagination[type].page + 1;
    const searchParams = { hitsPerPage: SECONDARY_HITS_PER_PAGE };
    if (isAgeGateRestrictedSearch()) {
      searchParams.filters = type === 'pages' ? AGE_GATE_CONTENT_FILTER : AGE_GATE_ARTICLE_FILTER;
    }
    searchParams.filters = appendAlgoliaFilter(
      searchParams.filters,
      getTemporaryLanguageFilter(type),
    );

    const index = client.initIndex(indexName);
    const res = await searchFilteredSecondaryPage(
      index,
      type,
      _secondaryQuery,
      searchParams,
      nextPage,
    );

    _secondaryPagination[type].page = res.page;
    _secondaryPagination[type].nbPages = res.nbPages;
    _secondaryPagination[type].loaded += res.hits.length;

    const listEl = document.querySelector(`.js-search-results--${type}`);
    if (listEl && btn) {
      const fragment = res.hits.map(hit => renderContentHit(hit, type)).join('');
      // Insert before the load more button
      btn.insertAdjacentHTML('beforebegin', fragment);
    }
  } catch (err) {
    console.warn(`[SecondarySearch] load more ${type} failed:`, err);
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = T.loadMore(); }
    updateLoadMoreBtn(type);
  }
}

async function querySecondaryIndices(query) {
  const client = getSecondaryClient();
  if (!client) return;

  _secondaryQuery = query;
  _secondaryPagination.pages    = { page: 0, nbPages: 1, loaded: 0, total: 0 };
  _secondaryPagination.articles = { page: 0, nbPages: 1, loaded: 0, total: 0 };

  const prefix = resolveIndexPrefix();

  try {
    const collectionParams = { hitsPerPage: 20 };
    const pageParams = { hitsPerPage: SECONDARY_HITS_PER_PAGE };
    const articleParams = { hitsPerPage: SECONDARY_HITS_PER_PAGE };

    if (isAgeGateRestrictedSearch()) {
      collectionParams.filters = AGE_GATE_CONTENT_FILTER;
      pageParams.filters = AGE_GATE_CONTENT_FILTER;
      articleParams.filters = AGE_GATE_ARTICLE_FILTER;
    }
    articleParams.filters = appendAlgoliaFilter(
      articleParams.filters,
      getTemporaryLanguageFilter('articles'),
    );

    const collectionIndex = client.initIndex(prefix + 'collections');
    const pageIndex = client.initIndex(prefix + 'pages');
    const articleIndex = client.initIndex(prefix + 'articles');

    const [colRes, pageRes, artRes, eligibleCollectionCount, eligiblePageCount] = await Promise.all([
      collectionIndex.search(query, collectionParams),
      searchFilteredSecondaryPage(pageIndex, 'pages', query, pageParams),
      searchFilteredSecondaryPage(articleIndex, 'articles', query, articleParams),
      getTemporaryEligibleHitCount(
        collectionIndex,
        'collections',
        query,
        collectionParams.filters,
      ),
      getTemporaryEligibleHitCount(pageIndex, 'pages', query, pageParams.filters),
    ]);

    const eligibleCollectionHits = filterTemporaryLanguageHits('collections', colRes.hits);
    const brandHits = eligibleCollectionHits.filter(h => h.template_suffix === 'brand');
    const collectionHits = eligibleCollectionHits.filter(h => h.template_suffix !== 'brand');
    const collectionTotal = eligibleCollectionCount ?? colRes.nbHits;
    const pageTotal = eligiblePageCount ?? pageRes.nbHits;
    const articleTotal = artRes.nbHits;

    renderCollectionsResults(collectionTotal, collectionHits, brandHits);

    _secondaryPagination.pages = {
      page: pageRes.page,
      nbPages: pageRes.nbPages,
      loaded: pageRes.hits.length,
      total: pageTotal,
    };
    _secondaryPagination.articles = {
      page: artRes.page,
      nbPages: artRes.nbPages,
      loaded: artRes.hits.length,
      total: articleTotal,
    };

    renderContentResults(
      '.js-search-tab-count--pages',
      '.js-search-results--pages',
      'pages',
      { ...pageRes, nbHits: pageTotal },
      hit => renderContentHit(hit, 'pages'),
    );
    renderContentResults(
      '.js-search-tab-count--articles',
      '.js-search-results--articles',
      'articles',
      { ...artRes, nbHits: articleTotal },
      hit => renderContentHit(hit, 'articles'),
    );

    _searchSecondaryNbHits = collectionTotal + pageTotal + articleTotal;
    setSearchState(query);
  } catch (err) {
    console.warn('[SecondarySearch] query failed:', err);
    _searchSecondaryNbHits = 0;
    setSearchState(query);
  }
}

function renderContentResults(countSel, listSel, type, res, renderHit) {
  const countEl = document.querySelector(countSel);
  const listEl  = document.querySelector(listSel);
  if (countEl) countEl.textContent = `(${res.nbHits})`;
  syncMobileSelectIfActive();
  if (!listEl) return;

  const pagination = _secondaryPagination[type];
  const hasMore = pagination.loaded < pagination.total && pagination.page < pagination.nbPages - 1;
  const loadMoreButton = `<button class="ais-custom-load-more js-search-load-more--${type}" style="${hasMore ? '' : 'display:none'}">${T.loadMore()}</button>`;
  listEl.innerHTML = res.hits.length
    ? res.hits.map(renderHit).join('') + loadMoreButton
    : (hasMore ? loadMoreButton : `<p class="search-no-results">${T.noResultsFound()}</p>`);

  const btn = listEl.querySelector(`.js-search-load-more--${type}`);
  if (btn) {
    btn.addEventListener('click', () => {
      // PLP-001: load-more is not one of the three plp_interaction controls.
      loadMoreSecondary(type);
    });
  }
}

function renderCollectionsResults(totalCount, collectionHits, brandHits) {
  const countEl = document.querySelector('.js-search-tab-count--collections');
  const listEl  = document.querySelector('.js-search-results--collections');
  if (countEl) countEl.textContent = `(${totalCount})`;
  if (!listEl) return;

  const noResults = collectionHits.length === 0 && brandHits.length === 0;
  if (noResults) {
    listEl.innerHTML = `<p class="search-no-results">${T.noResultsFound()}</p>`;
    return;
  }

  const collectionsHtml = collectionHits.length
    ? collectionHits.map(hit =>
        `<a class="search-result-item" href="/collections/${hit.handle}">${hit._highlightResult?.title?.value || hit.title}</a>`
      ).join('')
    : '';

  const brandsHtml = brandHits.length
    ? brandHits.map(hit => {
        const img = hit.image || '';
        return `<a class="search-brand-tile" href="/collections/${hit.handle}">
          <div class="search-brand-tile__image">
            ${img ? `<img src="${img}" alt="${hit.title}" loading="lazy">` : ''}
          </div>
          <span class="search-brand-tile__title">${hit.title}</span>
        </a>`;
      }).join('')
    : '';

  listEl.innerHTML = `
    <div class="search-collections-layout">
      <div class="search-collections-list">
        <h3 class="search-secondary-subheader">${T.searchResourceCollections()}</h3>
        ${collectionsHtml}
      </div>
      <div class="search-brands-grid-wrap">
        <h3 class="search-secondary-subheader">${T.searchBrands()}</h3>
        <div class="search-brands-grid">${brandsHtml}</div>
      </div>
    </div>`;
}


function initSearchTabs() {
  const tabs   = document.querySelectorAll('.search-tabs__tab');
  const panels = document.querySelectorAll('.search-tabs__panel');

  function activateTab(tabName) {
    tabs.forEach(t => { t.classList.remove('search-tabs__tab--active'); t.setAttribute('aria-selected', 'false'); });
    panels.forEach(p => p.classList.remove('search-tabs__panel--active'));
    const tab = document.querySelector(`.search-tabs__tab[data-tab="${tabName}"]`);
    if (!tab) return;
    tab.classList.add('search-tabs__tab--active');
    tab.setAttribute('aria-selected', 'true');
    const panel = document.querySelector(`.search-tabs__panel[data-panel="${tabName}"]`);
    if (panel) panel.classList.add('search-tabs__panel--active');
  }

  tabs.forEach(tab => {
    tab.addEventListener('click', (event) => {
      activateTab(tab.dataset.tab);
      history.replaceState(null, '', `#${tab.dataset.tab}`);
      syncMobileSelectTrigger(document.querySelector('.search-tabs-select__trigger'));
      if (event.isTrusted) {
        // PLP-004: SRP content selector uses fixed English literals (no tab text).
        publishPlpAnalyticsEvent('ocs:plp_interaction', buildPlpInteractionPayload({
          interaction_type: 'SRP Content Selector',
          input_value: 'Collections & Brands',
        }));
      }
    });
  });

  // Restore tab from hash on load.
  // For non-products tabs, defer until after Algolia renders so facets aren't
  // initialized inside a display:none container.
  const hash = window.location.hash.slice(1);
  if (hash && document.querySelector(`.search-tabs__tab[data-tab="${hash}"]`)) {
    if (hash === 'products') {
      activateTab(hash);
    } else {
      _pendingHashTab = hash;
    }
  }

  buildMobileSelect();
}

function buildMobileSelect() {
  const nav = document.querySelector('.search-tabs');
  if (!nav) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'search-tabs-select';
  wrapper.setAttribute('role', 'listbox');

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'search-tabs-select__trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  const list = document.createElement('ul');
  list.className = 'search-tabs-select__list';

  document.querySelectorAll('.search-tabs__tab').forEach(tab => {
    const li = document.createElement('li');
    li.className = 'search-tabs-select__option';
    li.dataset.tab = tab.dataset.tab;
    li.setAttribute('role', 'option');
    li.textContent = tab.textContent.trim();
    if (tab.classList.contains('search-tabs__tab--active')) {
      li.classList.add('search-tabs-select__option--active');
      trigger.textContent = tab.textContent.trim();
    }
    li.addEventListener('click', () => {
      // PLP-004: SRP content selector uses fixed English literals (no tab text).
      publishPlpAnalyticsEvent('ocs:plp_interaction', buildPlpInteractionPayload({
        interaction_type: 'SRP Content Selector',
        input_value: 'Collections & Brands',
      }));
      tab.click();
      syncMobileSelectTrigger(trigger);
      closeSelect(wrapper, trigger);
    });
    list.appendChild(li);
  });

  const chevron = document.createElement('span');
  chevron.className = 'search-tabs-select__chevron';
  chevron.innerHTML = `<svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1L6 7L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  trigger.appendChild(chevron);

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = wrapper.classList.toggle('search-tabs-select--open');
    trigger.setAttribute('aria-expanded', String(isOpen));
  });

  document.addEventListener('click', () => closeSelect(wrapper, trigger));

  wrapper.appendChild(trigger);
  wrapper.appendChild(list);
  nav.insertAdjacentElement('afterend', wrapper);
}

function syncMobileSelectTrigger(trigger) {
  const activeTab = document.querySelector('.search-tabs__tab--active');
  if (!activeTab || !trigger) return;
  document.querySelectorAll('.search-tabs-select__option').forEach(opt => {
    const sourceTab = document.querySelector(`.search-tabs__tab[data-tab="${opt.dataset.tab}"]`);
    if (sourceTab) opt.textContent = sourceTab.textContent.trim();
    opt.classList.toggle('search-tabs-select__option--active', opt.dataset.tab === activeTab.dataset.tab);
  });
  const chevron = trigger.querySelector('.search-tabs-select__chevron');
  trigger.textContent = activeTab.textContent.trim();
  if (chevron) trigger.appendChild(chevron);
}

function closeSelect(wrapper, trigger) {
  wrapper.classList.remove('search-tabs-select--open');
  trigger.setAttribute('aria-expanded', 'false');
}

function syncMobileSelectIfActive() {
  const trigger = document.querySelector('.search-tabs-select__trigger');
  if (trigger) syncMobileSelectTrigger(trigger);
}

// =================================================================
// CARD UPDATE (imperative DOM: for InstantSearch hits template path)
// =================================================================

/** Update card UI based on Shopify variant data (source of truth) */
function updateCardFromShopifyData(card, variant, inStoreOverride) {
  const inStore =
    inStoreOverride !== undefined
      ? inStoreOverride
      : card.dataset.inStore === "true";

  const priceContainer = card.querySelector("[data-price-container]");
  if (priceContainer) {
    if (inStore) {
      priceContainer.innerHTML = `<p class="ais-hit--in-store-message">${T.inStoreContactRetailerHtml()}</p>`;
    } else {
      priceContainer.innerHTML = `<p class="ais-hit--price" data-price>${buildPriceHTML(variant.price, variant.compare_at_price)}</p>`;
    }
  }
  updateCardDiscountBadges(card, variant.price, variant.compare_at_price, inStore);
  updateCardPricePerUnit(card, variant.display_ppu, variant.display_uom, inStore);

  const cartButton = card.querySelector('.ais-hit--cart-button');
  if (cartButton) {
    applyCartButtonState(
      cartButton,
      variant.available,
      variant.available && !inStore
        ? function (e) {
            e.preventDefault();
            e.stopPropagation();
            algoliaAddToCart(card.dataset.variantId, 1, this);
          }
        : null,
      inStore,
    );
  }
}

/** Handle swatch click: update active state, price, and cart button */
function algoliaSelectVariant(e) {
  e.preventDefault();
  e.stopPropagation();

  const swatch = e.currentTarget;
  const card = swatch.closest(".ais-hit");
  const swatchesContainer = swatch.closest("[data-swatches]");

  const variantId = swatch.dataset.variantId;
  const variantPrice = parseInt(swatch.dataset.variantPrice, 10);
  const variantComparePrice = swatch.dataset.variantComparePrice
    ? parseInt(swatch.dataset.variantComparePrice, 10)
    : null;
  const variantAvailable = swatch.dataset.variantAvailable === "true";
  const variantPpu = swatch.dataset.variantPpu || null;
  const variantUom = swatch.dataset.variantUom || null;

  swatchesContainer
    .querySelectorAll('[data-action="select-variant"]')
    .forEach((s) => s.classList.remove("ais-hit--swatch-active"));
  swatch.classList.add("ais-hit--swatch-active");
  card.dataset.variantId = variantId;
  const selectedVariant = findProductVariant(card._ocsProduct, variantId) || { id: variantId };
  syncPlpCardSelectedVariantAnalytics(card, selectedVariant);
  // PLP-001: swatch selection is a variant interaction on the card, not a
  // plp_interaction; the matcher item ID stays synchronized on the card dataset.

  const inStore =
    swatch.dataset.variantInStore === "true" ||
    card.dataset.inStore === "true";

  const priceContainer = card.querySelector("[data-price-container]");
  if (priceContainer) {
    if (inStore) {
      priceContainer.innerHTML = `<p class="ais-hit--in-store-message">${T.inStoreContactRetailerHtml()}</p>`;
    } else {
      priceContainer.innerHTML = `<p class="ais-hit--price" data-price>${buildPriceHTML(variantPrice, variantComparePrice)}</p>`;
    }
  }
  updateCardDiscountBadges(card, variantPrice, variantComparePrice, inStore);
  updateCardPricePerUnit(card, variantPpu, variantUom, inStore);

  const cartButton = card.querySelector('.ais-hit--cart-button');
  if (cartButton) {
    applyCartButtonState(
      cartButton,
      variantAvailable,
      variantAvailable && !inStore
        ? function (e) {
            e.preventDefault();
            e.stopPropagation();
            algoliaAddToCart(card.dataset.variantId, 1, this);
          }
        : null,
      inStore,
    );
  }
}

/**
 * Render all variant swatches for a product card (max 3 shown).
 * Uses Shopify product data as source of truth for availability.
 */
function algoliaRenderSwatches(card, swatchesContainer, product) {
  const selectedVariant = selectBestVariant(product);
  card._ocsProduct = product;
  if (selectedVariant) {
    card.dataset.variantId = selectedVariant.id.toString();
    syncPlpCardSelectedVariantAnalytics(card, selectedVariant);
  }

  swatchesContainer.innerHTML = buildSwatchesHTML(
    product,
    selectedVariant,
    card.dataset.handle,
    card.dataset.inStore === "true",
  );
  swatchesContainer.dataset.loaded = "true";
  swatchesContainer.dataset.loadedHandle = card.dataset.handle;
  swatchesContainer.dataset.loadedObjectId = card.dataset.algoliaObjectid;
  swatchesContainer.classList.remove("ais-hit--swatches-loading");

  swatchesContainer
    .querySelectorAll('[data-action="select-variant"]')
    .forEach((swatch) => {
      swatch.addEventListener("click", algoliaSelectVariant);
    });

  if (selectedVariant) {
    const activeSwatch = swatchesContainer.querySelector(
      ".ais-hit--swatch-active",
    );
    const inStore =
      activeSwatch?.dataset.variantInStore === "true" ||
      card.dataset.inStore === "true";
    updateCardFromShopifyData(card, selectedVariant, inStore);
  }
}

/**
 * Re-render swatches on all already-loaded cards when ACF toggles change.
 * Needed because IS (lit-html) patches DOM in place, preserving data-loaded="true",
 * so algoliaProcessFetchQueue skips re-rendering after toggle state changes.
 */
document.addEventListener("acf:toggles-changed", function () {
  document.querySelectorAll('[data-swatches][data-loaded="true"]').forEach(function (container) {
    const card = container.closest("[data-algolia-objectid]");
    if (!card) return;
    const handle = card.dataset.handle;
    const locale = window.Shopify?.locale || "en";
    const product = algoliaProductCache[handle + "__" + locale];
    if (!product) return;
    algoliaRenderSwatches(card, container, product);
  });
});

function algoliaQueueVariantFetch(handle, cardId) {
  algoliaFetchQueue.push({ handle, cardId });
  requestAnimationFrame(() => {
    algoliaPrepareHydrationTarget(handle, cardId);
    return algoliaProcessFetchQueue();
  });
}

function algoliaPrepareHydrationTarget(handle, cardId) {
  const objectId = String(cardId);
  const card = document.querySelector(
    `[data-algolia-objectid="${objectId}"]`,
  );

  if (
    !card ||
    card.dataset.handle !== handle ||
    card.dataset.algoliaObjectid !== objectId
  ) {
    return null;
  }

  const swatchesContainer = card.querySelector("[data-swatches]");
  if (!swatchesContainer) return null;

  const previousTarget = card._ocsHydrationTarget;
  const targetChanged =
    !previousTarget ||
    previousTarget.handle !== handle ||
    previousTarget.objectId !== objectId ||
    previousTarget.swatchesContainer !== swatchesContainer;

  if (targetChanged) {
    const generation = (Number(card._ocsHydrationGeneration) || 0) + 1;
    card._ocsHydrationGeneration = generation;
    card._ocsHydrationTarget = {
      handle,
      objectId,
      swatchesContainer,
      generation,
    };
  }

  const loadedIdentityMatches =
    swatchesContainer.dataset.loaded === "true" &&
    swatchesContainer.dataset.loadedHandle === handle &&
    swatchesContainer.dataset.loadedObjectId === objectId;
  if (loadedIdentityMatches) {
    const activeSwatch = swatchesContainer.querySelector(
      ".ais-hit--swatch-active",
    );
    const selectedVariantId =
      activeSwatch?.dataset.variantId || card.dataset.variantId;
    const selectedVariant = findProductVariant(
      card._ocsProduct,
      selectedVariantId,
    );

    if (selectedVariant) {
      card.dataset.variantId = String(selectedVariant.id);
      syncPlpCardSelectedVariantAnalytics(card, selectedVariant);
      const inStore =
        activeSwatch?.dataset.variantInStore === "true" ||
        card.dataset.inStore === "true";
      updateCardFromShopifyData(card, selectedVariant, inStore);
      return {
        card,
        swatchesContainer,
        generation: card._ocsHydrationTarget.generation,
        loaded: true,
      };
    }
  }

  const resetAlreadyPrepared =
    swatchesContainer.dataset.loaded === "false" &&
    swatchesContainer.dataset.loadedHandle === undefined &&
    swatchesContainer.dataset.loadedObjectId === undefined &&
    card._ocsProduct === null &&
    card.dataset.itemId === "" &&
    swatchesContainer.innerHTML ===
      '<span class="ais-hit--swatch-placeholder"></span>' &&
    swatchesContainer.classList.contains("ais-hit--swatches-loading");

  if (!targetChanged && !resetAlreadyPrepared) {
    const generation = (Number(card._ocsHydrationGeneration) || 0) + 1;
    card._ocsHydrationGeneration = generation;
    card._ocsHydrationTarget.generation = generation;
  }

  card._ocsProduct = null;
  syncPlpCardSelectedVariantAnalytics(card, null);
  swatchesContainer.innerHTML =
    '<span class="ais-hit--swatch-placeholder"></span>';
  swatchesContainer.dataset.loaded = "false";
  delete swatchesContainer.dataset.loadedHandle;
  delete swatchesContainer.dataset.loadedObjectId;
  swatchesContainer.classList.add("ais-hit--swatches-loading");

  return {
    card,
    swatchesContainer,
    generation: card._ocsHydrationTarget.generation,
    loaded: false,
  };
}

function algoliaHydrationTargetIsCurrent(
  card,
  swatchesContainer,
  handle,
  cardId,
  generation,
) {
  const objectId = String(cardId);
  const currentCard = document.querySelector(
    `[data-algolia-objectid="${objectId}"]`,
  );

  return (
    currentCard === card &&
    card.dataset.handle === handle &&
    card.dataset.algoliaObjectid === objectId &&
    card.querySelector("[data-swatches]") === swatchesContainer &&
    card._ocsHydrationTarget?.generation === generation
  );
}

async function algoliaProcessFetchQueue() {
  if (algoliaFetchInProgress || algoliaFetchQueue.length === 0) return;

  algoliaFetchInProgress = true;

  while (algoliaFetchQueue.length > 0) {
    const { handle, cardId } = algoliaFetchQueue.shift();
    const objectId = String(cardId);
    const target = algoliaPrepareHydrationTarget(handle, objectId);
    if (!target || target.loaded) continue;

    const { card, swatchesContainer, generation } = target;

    try {
      const product = await algoliaFetchProduct(handle);
      if (
        !algoliaHydrationTargetIsCurrent(
          card,
          swatchesContainer,
          handle,
          objectId,
          generation,
        )
      ) {
        continue;
      }
      algoliaRenderSwatches(card, swatchesContainer, product);
    } catch (error) {
      console.error(`Failed to load variants for ${handle}:`, error);
      if (
        algoliaHydrationTargetIsCurrent(
          card,
          swatchesContainer,
          handle,
          objectId,
          generation,
        )
      ) {
        swatchesContainer.classList.remove("ais-hit--swatches-loading");
      }
    }
  }

  algoliaFetchInProgress = false;
}

// =================================================================
// CART
// =================================================================

/**
 * AJAX Add to Cart.
 * Dispatches cart:update event to match theme's event system.
 */
async function algoliaAddToCart(variantId, quantity = 1, button) {
  if (!button) return;

  const card = button.closest(".ais-hit, algolia-product-card");
  const productId = card?.dataset?.handle || "";
  const itemCount = parseInt(String(quantity), 10) || 1;
  let requestFailed = false;

  const isUnavailableState = () => {
    const state = button.getAttribute("data-state");
    return (
      state === "oos" ||
      state === "in-store" ||
      button.classList.contains("ais-hit--cart-button__oos") ||
      button.classList.contains("ais-hit--cart-button__in-store")
    );
  };

  try {
    button.disabled = true;
    button.classList.add("ais-hit--cart-button__loading");

    const sectionIds = getCartSectionIds();
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", itemCount);

    const analyticsApi = window.OCSAnalyticsEvents;
    let analyticsPayload = null;
    if (
      analyticsApi &&
      typeof analyticsApi.setCartLineProperties === 'function' &&
      typeof analyticsApi.createAddToCartPayload === 'function' &&
      typeof analyticsApi.publishOcsAnalyticsEvent === 'function'
    ) {
      const rawItem = {
        itemId: card?.dataset?.itemId,
        itemName: card?.dataset?.itemName,
        brand: card?.dataset?.itemBrand,
        itemVariant: card?.dataset?.itemVariant,
        category: card?.dataset?.itemCategory,
        category2: card?.dataset?.itemCategory2,
        category3: card?.dataset?.itemCategory3,
        ontarioGrown: card?.dataset?.ontarioGrown,
        craft: card?.dataset?.craft,
        priceCents: card?.dataset?.priceCents,
        compareAtCents: card?.dataset?.compareAtPriceCents,
        variantStatus: card?.dataset?.variantStatus,
        variantAvailable: card?.dataset?.variantAvailable,
        variantInStore: card?.dataset?.inStore,
        quantity,
      };
      const originBoundary = typeof card?.closest === 'function'
        ? card.closest('.algolia-collection[data-ocs-origin-kind][data-ocs-item-list-name]')
        : null;
      const originKind = originBoundary?.dataset?.ocsOriginKind;
      const itemListName = originBoundary?.dataset?.ocsItemListName;
      const itemListIsSafe =
        typeof itemListName === 'string' &&
        itemListName.trim().length > 0 &&
        Array.from(itemListName).length <= 200 &&
        !/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060\u2066-\u2069\ufeff]/u.test(itemListName);
      const originPairMatches =
        (originKind === 'search' && itemListName === 'Search') ||
        (originKind === 'shop_all' && itemListName === 'All') ||
        (originKind === 'collection' && itemListIsSafe);
      if (originPairMatches) rawItem.itemListName = itemListName;

      const persistenceItem = originPairMatches
        ? rawItem
        : { ...rawItem, itemListName: undefined };
      try {
        for (const key of [
          '_item_id',
          '_item_category',
          '_item_category2',
          '_item_category3',
          '_item_brand',
          '_ontario_grown',
          '_craft',
          '_price_drop',
          '_new',
          '_item_list_name',
        ]) {
          formData.delete(`properties[${key}]`);
        }
        analyticsApi.setCartLineProperties(formData, persistenceItem);
        const builtPayload = analyticsApi.createAddToCartPayload([rawItem], 'PLP');
        analyticsPayload = originPairMatches ? builtPayload : null;
      } catch (_) {
        analyticsPayload = null;
      }
    }

    if (sectionIds.length > 0) {
      formData.append("sections", sectionIds.join(","));
      formData.append("sections_url", window.location.pathname);
    }

    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (response.ok) {
      if (analyticsPayload) {
        try {
          analyticsApi.publishOcsAnalyticsEvent('ocs:add_to_cart', analyticsPayload);
        } catch (_) {}
      }

      let lineItem = data;

      // cart/add.js may return an incomplete payload on some stores/apps.
      // Fall back to /cart.js so toast/drawer feedback receives a complete line item.
      if (!lineItem || typeof lineItem.price !== "number" || !lineItem.title) {
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + "cart.js");
          const cart = await cartResponse.json();
          const selectedVariantId = Number(variantId);
          lineItem = cart.items?.find((item) => item.variant_id === selectedVariantId) || lineItem;
        } catch (_) {
          // fallback failed, keep the cart/add.js payload
        }
      }

      // Dispatch cart:update event (same as theme's CartAddEvent).
      // Use 'product-form-component' as source so cart icon adds to count instead of replacing.
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: lineItem,
            sourceId: `algolia-product-${variantId}`,
            data: {
              source: "product-form-component",
              itemCount,
              productId,
              variantId: variantId.toString(),
              sections: data?.sections || {},
            },
          },
        }),
      );

      let toastHandled = false;
      const toastEl = document.querySelector("ocs-cart-toast-component");
      if (
        toastEl &&
        typeof toastEl.showForItem === "function" &&
        lineItem &&
        typeof lineItem.price === "number" &&
        lineItem.title
      ) {
        toastEl.showForItem(lineItem);
        toastHandled = true;
      }

      if (!toastHandled) {
        const cartDrawer = document.querySelector(
          "ocs-cart-drawer-component, cart-drawer-component",
        );
        if (cartDrawer) {
          if (typeof cartDrawer.open === "function") cartDrawer.open();
          else if (typeof cartDrawer.showDialog === "function")
            cartDrawer.showDialog();
        }
      }

    } else {
      throw new Error(data?.message || data?.description || T.failedAddToCart());
    }
  } catch (error) {
    requestFailed = true;
    console.error("Add to cart error:", error);
    button.classList.add("ais-hit--cart-button__error");

    document.dispatchEvent(
      new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          sourceId: `algolia-product-${variantId}`,
          data: { message: error?.message || T.failedAddToCart() },
        },
      }),
    );

    setTimeout(() => {
      button.classList.remove("ais-hit--cart-button__error");
      if (!isUnavailableState()) button.disabled = false;
    }, CART_ERROR_RESET_MS);
  } finally {
    button.classList.remove("ais-hit--cart-button__loading");
    if (!requestFailed && !isUnavailableState()) button.disabled = false;
  }
}

// =================================================================
// PRODUCT CARD WEB COMPONENT
// =================================================================

/**
 * Update save % and PRICE DROP badges on a card element based on current variant pricing.
 * Shared between AlgoliaProductCard.updatePriceDisplay and algoliaSelectVariant.
 */
function updateCardDiscountBadges(card, price, compareAtPrice, inStore = false) {
  const hasDiscount = !!(compareAtPrice && compareAtPrice > 0 && compareAtPrice > price);

  const pricingMeta = card.querySelector(".ais-hit--pricing-meta");
  if (pricingMeta) {
    pricingMeta.style.display = inStore ? "none" : "";
    const existing = pricingMeta.querySelector(".ais-hit--save-badge");
    if (existing) existing.remove();
    if (!inStore) {
      const percent = hasDiscount ? calculateSavePercentage(price, compareAtPrice) : null;
      if (percent && percent > SAVE_PERCENTAGE_THRESHOLD) {
        pricingMeta.insertAdjacentHTML("afterbegin", `<span class="ais-hit--save-badge">${T.savePercent(percent)}</span>`);
      }
    }
  }

  const existingPriceDropBadge = card.querySelector(".ais-hit--badge-pricedrop");
  if (hasDiscount && !existingPriceDropBadge) {
    let badgesContainer = card.querySelector(".ais-hit--badges");
    if (!badgesContainer) {
      card.insertAdjacentHTML("afterbegin", `<div class="ais-hit--badges" role="list" aria-label="${T.productBadgesLabel()}"></div>`);
      badgesContainer = card.querySelector(".ais-hit--badges");
    }
    badgesContainer.insertAdjacentHTML("beforeend", `<span class="ais-hit--badge ais-hit--badge-pricedrop" role="listitem">${T.discountLabel()}</span>`);
  } else if (!hasDiscount && existingPriceDropBadge) {
    existingPriceDropBadge.remove();
    const badgesContainer = card.querySelector(".ais-hit--badges");
    if (badgesContainer && !badgesContainer.children.length) badgesContainer.remove();
  }
}

/** Update or remove the PPU element when the selected variant changes */
function updateCardPricePerUnit(card, ppu, unit, inStore) {
  const pricingMeta = card.querySelector(".ais-hit--pricing-meta");
  if (!pricingMeta) return;
  const existing = pricingMeta.querySelector(".ais-hit--ppu");
  const hasPpu = !inStore && ppu && parseFloat(ppu) > 0 && unit;
  if (!hasPpu) {
    if (existing) existing.remove();
    return;
  }
  const formatted = `(${algoliaFormatPPU(ppu)} / ${unit})`;
  if (existing) {
    existing.textContent = formatted;
  } else {
    // insertAdjacentHTML("beforeend") keeps PPU after the save-badge, matching initial render order
    pricingMeta.insertAdjacentHTML("beforeend", `<p class="ais-hit--ppu">${formatted}</p>`);
  }
}

/**
 * AlgoliaProductCard Web Component
 * Encapsulates all product card logic including swatches and add-to-cart.
 * Used by the Load More path where cards are created programmatically.
 */
class AlgoliaProductCard extends HTMLElement {
  static get observedAttributes() {
    return ["variant-id"];
  }

  constructor() {
    super();
    this._hit = null;
    this._product = null;
    this._variantFetchGeneration = 0;
  }

  set hit(data) {
    const previousHandle = this._hit?.handle;
    const previousObjectId = String(this._hit?.objectID || '');
    const previousVariantId = this.variantId;
    const nextObjectId = String(data?.objectID || '');
    const identityChanged =
      previousHandle !== data?.handle || previousObjectId !== nextObjectId;

    this._hit = data;
    if (identityChanged) this._product = null;
    this.render();

    if (!this.isConnected) return;
    if (!identityChanged && this._product) {
      if (findProductVariant(this._product, previousVariantId)) {
        this.setAttribute('variant-id', previousVariantId);
      }
      this.renderSwatches();
      return;
    }
    this.fetchVariants();
  }

  get hit() {
    return this._hit;
  }

  get variantId() {
    return this.getAttribute("variant-id");
  }

  connectedCallback() {
    if (!this._hit) return;
    // Skip re-render if hit setter already rendered before connection
    if (!this.children.length) this.render();
    this.fetchVariants();
  }

  disconnectedCallback() {
    this._variantFetchGeneration += 1;
  }

  isCurrentVariantFetch(generation, handle, objectId) {
    return (
      generation === this._variantFetchGeneration &&
      this._hit?.handle === handle &&
      String(this._hit?.objectID || '') === objectId &&
      this.isConnected !== false
    );
  }

  render() {
    const hit = this._hit;
    if (!hit) return;

    this.setAttribute("data-algolia-objectid", hit.objectID);
    this.setAttribute("data-handle", hit.handle);
    this.setAttribute("data-ocs-item-index", String(algoliaHitViewIndex(hit)));
    this.setAttribute("variant-id", hit.objectID);
    this.setAttribute("data-in-store", isInStoreOnly(hit) ? "true" : "false");
    this.setAttribute("data-product-tags", (hit.tags || hit._tags || []).join(','));
    // Blank until the endpoint matcher resolves; never the Algolia objectID.
    this.setAttribute("data-item-id", "");
    this.setAttribute("data-item-name", algoliaHitTitle(hit));

    // Authoritative nested metadata, verbatim from hit.meta.
    const analytics = algoliaCardAnalyticsAttributes(hit);
    this.setAttribute("data-item-brand", analytics.brand);
    this.setAttribute("data-item-category", analytics.category);
    this.setAttribute("data-item-category2", analytics.category2);
    this.setAttribute("data-item-category3", analytics.category3);
    this.setAttribute("data-ontario-grown", analytics.ontarioGrown);
    this.setAttribute("data-craft", analytics.craft);
    this.setAttribute("data-variant-status", analytics.variantStatus);
    // Both attributes stay absent when their authority is malformed.
    if (analytics.priceDrop) this.setAttribute("data-price-drop", analytics.priceDrop);
    if (analytics.complete) this.setAttribute("data-analytics-complete", "true");

    // A new card identity must not retain a selected snapshot from the prior hit.
    syncPlpCardSelectedVariantAnalytics(this, null);
    this.className = "ais-hit ais-product";

    const productUrl = hitProductUrl(hit);
    const title = algoliaHitTitle(hit);
    this.innerHTML = `
      ${renderBadgesHTML(hit)}
      <div class="ais-hit--info">
        <div class="ais-hit--media">
          <a href="${productUrl}" class="ais-hit--link">
            <img class="ais-hit--picture" src="${hitImageUrl(hit)}" alt="${title}" width="784" height="781" loading="lazy" />
          </a>
          ${renderOntarioGrownBadgeHTML(hit)}
        </div>
        <div class="ais-hit--details">
          ${hit.vendor ? `<p class="ais-hit--vendor">${hit.vendor}</p>` : ""}
          <p class="ais-hit--title">
            <a href="${productUrl}" title="${title}">${title}</a>
          </p>
          ${renderPlantTypeHTML(hit)}
        </div>
      </div>
      <div class="ais-hit--actions">
        ${renderCannabinoidInfoHTML(hit)}
        <div class="ais-hit--swatches ais-hit--swatches-loading" data-swatches data-loaded="false">
          <span class="ais-hit--swatch-placeholder"></span>
        </div>
        <div class="ais-hit--pricing">
          ${renderPriceDisplayHTML(hit)}
          <div class="ais-hit--pricing-meta">
            ${renderSaveBadgeHTML(hit)}
            ${renderPricePerUnitHTML(hit)}
          </div>
        </div>
        <div class="ais-hit--cart">
          ${buildCartButtonHTML(isAvailableForOrder(hit), isInStoreOnly(hit))}
        </div>
      </div>
    `;
  }

  async fetchVariants() {
    const handle = this._hit?.handle;
    const objectId = String(this._hit?.objectID || '');
    if (!handle || !objectId) return;

    const generation = this._variantFetchGeneration + 1;
    this._variantFetchGeneration = generation;
    this._product = null;
    syncPlpCardSelectedVariantAnalytics(this, null);
    try {
      const product = await algoliaFetchProduct(handle);
      if (!this.isCurrentVariantFetch(generation, handle, objectId)) return;
      this._product = product;
      this.renderSwatches();
    } catch (error) {
      if (!this.isCurrentVariantFetch(generation, handle, objectId)) return;
      console.error(`Failed to load variants for ${handle}:`, error);
      const swatches = this.querySelector("[data-swatches]");
      if (swatches) swatches.classList.remove("ais-hit--swatches-loading");
    }
  }

  renderSwatches() {
    const swatchesContainer = this.querySelector("[data-swatches]");
    if (!swatchesContainer || !this._product) return;

    const selectedVariant = selectBestVariant(this._product);
    if (selectedVariant) {
      this.setAttribute("variant-id", selectedVariant.id.toString());
      syncPlpCardSelectedVariantAnalytics(this, selectedVariant);
    }

    swatchesContainer.innerHTML = buildSwatchesHTML(
      this._product,
      selectedVariant,
      this._hit.handle,
      isInStoreOnly(this._hit),
    );
    swatchesContainer.dataset.loaded = "true";
    swatchesContainer.classList.remove("ais-hit--swatches-loading");

    swatchesContainer
      .querySelectorAll('[data-action="select-variant"]')
      .forEach((swatch) => {
        swatch.addEventListener("click", (e) => this.handleSwatchClick(e));
      });

    if (selectedVariant) {
      const activeSwatch = swatchesContainer.querySelector(
        ".ais-hit--swatch-active",
      );
      const inStore =
        activeSwatch?.dataset.variantInStore === "true" ||
        isInStoreOnly(this._hit);
      this.updatePriceDisplay(
        selectedVariant.price,
        selectedVariant.compare_at_price,
        inStore,
      );
      this.updateCartButton(selectedVariant.available, inStore);
      updateCardPricePerUnit(this, selectedVariant.display_ppu, selectedVariant.display_uom, inStore);
    }
  }

  handleSwatchClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const swatch = e.currentTarget;
    const swatchesContainer = swatch.closest("[data-swatches]");
    const variantId = swatch.dataset.variantId;
    const variantPrice = parseInt(swatch.dataset.variantPrice, 10);
    const variantComparePrice = swatch.dataset.variantComparePrice
      ? parseInt(swatch.dataset.variantComparePrice, 10)
      : null;
    const variantAvailable = swatch.dataset.variantAvailable === "true";
    const variantInStore = swatch.dataset.variantInStore === "true";
    const variantPpu = swatch.dataset.variantPpu || null;
    const variantUom = swatch.dataset.variantUom || null;

    swatchesContainer
      .querySelectorAll('[data-action="select-variant"]')
      .forEach((s) => s.classList.remove("ais-hit--swatch-active"));
    swatch.classList.add("ais-hit--swatch-active");
    this.setAttribute("variant-id", variantId);
    const selectedVariant = findProductVariant(this._product, variantId) || { id: variantId };
    syncPlpCardSelectedVariantAnalytics(this, selectedVariant);
    // PLP-001: swatch selection is a variant interaction on the card, not a
    // plp_interaction; the matcher item ID stays synchronized on the card dataset.

    this.updatePriceDisplay(variantPrice, variantComparePrice, variantInStore);
    this.updateCartButton(variantAvailable, variantInStore);
    updateCardPricePerUnit(this, variantPpu, variantUom, variantInStore);
  }

  updatePriceDisplay(price, compareAtPrice, inStore) {
    const priceContainer = this.querySelector("[data-price-container]");
    if (priceContainer) {
      if (inStore) {
        priceContainer.innerHTML = `<p class="ais-hit--in-store-message">${T.inStoreContactRetailerHtml()}</p>`;
      } else {
        priceContainer.innerHTML = `<p class="ais-hit--price" data-price>${buildPriceHTML(price, compareAtPrice)}</p>`;
      }
    }
    updateCardDiscountBadges(this, price, compareAtPrice, inStore);
  }

  updateCartButton(isAvailable, inStoreOverride) {
    const cartButton = this.querySelector('.ais-hit--cart-button');
    if (!cartButton) return;
    const inStore =
      inStoreOverride !== undefined
        ? inStoreOverride
        : isInStoreOnly(this._hit || {});
    applyCartButtonState(
      cartButton,
      isAvailable,
      isAvailable && !inStore ? (e) => this.handleAddToCart(e) : null,
      inStore,
    );
  }

  async handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation();
    await algoliaAddToCart(this.variantId, 1, e.currentTarget);
  }
}

// -----------------------------------------------------------------
// Authoritative Algolia card metadata
// -----------------------------------------------------------------
// Shared by both card render paths: the Load More AlgoliaProductCard above and
// the classic InstantSearch product template below.
//
// Analytics attributes on Algolia cards come only from the nested snapshot
// indexed under hit.meta. Vendor, product type, and product tags are never
// analytics authorities. Values are emitted verbatim: no casing, boolean, or
// label normalization happens at render time.
const AUTHORITATIVE_META_PATHS = {
  brand: "meta.product.brand",
  category: "meta.product.category",
  category2: "meta.product.subcategory",
  category3: "meta.product.subsubcategory",
  ontarioGrown: "meta.product.ontario_grown",
  craft: "meta.product.craft",
  variantStatus: "meta.variant.variant_status",
  availability: "meta.variant.availability",
};

// Exact indexed vocabularies. Any other value is malformed, never coerced.
const AUTHORITATIVE_BADGE_VALUES = ["true", "false"];
const AUTHORITATIVE_AVAILABILITY_VALUES = ["available", "in-store"];

function readHitPath(hit, path) {
  return path
    .split(".")
    .reduce((node, key) => (node == null ? undefined : node[key]), hit);
}

function isAuthoritativeText(value) {
  return typeof value === "string" && value.trim() !== "";
}

// Missing or non-string authorities render blank so the add path never reads
// a coerced "undefined" string back out of the dataset.
function authoritativeAttributeValue(value) {
  return typeof value === "string" ? value : "";
}

// Yes only when a numeric compare-at price is greater than the numeric price.
// Absent, blank, zero, equal, and lower all mean No. A non-numeric compare-at
// price is malformed: null suppresses the attribute instead of guessing.
function authoritativePriceDrop(hit) {
  const price = Number(hit?.price);
  if (!Number.isFinite(price)) return null;

  const rawCompareAt = hit?.compare_at_price;
  const compareAt =
    rawCompareAt === undefined || rawCompareAt === null || rawCompareAt === ""
      ? 0
      : Number(rawCompareAt);
  if (!Number.isFinite(compareAt)) return null;

  return compareAt > price ? "Yes" : "No";
}

function algoliaCardAnalyticsAttributes(hit) {
  const raw = {};
  for (const key of Object.keys(AUTHORITATIVE_META_PATHS)) {
    raw[key] = readHitPath(hit, AUTHORITATIVE_META_PATHS[key]);
  }

  const priceDrop = authoritativePriceDrop(hit);
  const complete =
    priceDrop !== null &&
    isAuthoritativeText(raw.brand) &&
    isAuthoritativeText(raw.category) &&
    isAuthoritativeText(raw.category2) &&
    isAuthoritativeText(raw.category3) &&
    isAuthoritativeText(raw.variantStatus) &&
    AUTHORITATIVE_BADGE_VALUES.indexOf(raw.ontarioGrown) !== -1 &&
    AUTHORITATIVE_BADGE_VALUES.indexOf(raw.craft) !== -1 &&
    AUTHORITATIVE_AVAILABILITY_VALUES.indexOf(raw.availability) !== -1;

  return {
    brand: authoritativeAttributeValue(raw.brand),
    category: authoritativeAttributeValue(raw.category),
    category2: authoritativeAttributeValue(raw.category2),
    category3: authoritativeAttributeValue(raw.category3),
    ontarioGrown: authoritativeAttributeValue(raw.ontarioGrown),
    craft: authoritativeAttributeValue(raw.craft),
    variantStatus: authoritativeAttributeValue(raw.variantStatus),
    priceDrop,
    complete,
  };
}

// FR storefronts render the indexed FR title. Both render paths resolve it the
// same way so Load More cards match the classic InstantSearch cards.
function algoliaHitTitle(hit) {
  const isFR = window.Shopify?.locale?.startsWith("fr");
  const frTitle = hit?.meta?.translation?.["fr-CA_title"];
  return (isFR && frTitle) || hit?.title;
}

if (!customElements.get("algolia-product-card")) {
  customElements.define("algolia-product-card", AlgoliaProductCard);
}

// =================================================================

// HOOKS & SETUP
// =================================================================

/** Captured InstantSearch helper: set once on init, used by Load More to read active filters */
let _instantSearchHelper = null;

/** Default Algolia index name: captured on init, used to reset sort */
let _defaultSearchIndex = null;

/** Tab to activate once Algolia has rendered: prevents hiding the products panel before facets render */
let _pendingHashTab = null;

/** Products hit count from last Algolia stats render: -1 = not yet received */
let _searchProductsNbHits = -1;
/** Secondary (collections + pages + articles) hit count: -1 = not yet received */
let _searchSecondaryNbHits = -1;

function setSearchState(query) {
  const section = document.querySelector('.algolia-collection--search');
  if (!section) return;

  if (!query.trim()) {
    section.dataset.searchState = 'idle';
    return;
  }

  // Products have results: show tabs immediately, no need to wait for secondary
  if (_searchProductsNbHits > 0) {
    section.dataset.searchState = 'results';
    window.OCSSearchAnalytics?.reportSearchPageResult?.(query, true);
    // Nudge sticky facets to recalculate after becoming visible
    requestAnimationFrame(() => window.dispatchEvent(new Event('scroll')));
    return;
  }

  // Products empty: wait for secondary before deciding
  if (_searchProductsNbHits < 0 || _searchSecondaryNbHits < 0) return;

  const hasSearchResults = _searchProductsNbHits + _searchSecondaryNbHits > 0;
  window.OCSSearchAnalytics?.reportSearchPageResult?.(query, hasSearchResults);

  if (!hasSearchResults) {
    section.dataset.searchState = 'no-results';
    const headingEl = section.querySelector('.js-search-no-results-heading');
    if (headingEl) headingEl.textContent = T.searchNoResultsFor(query);
  } else {
    section.dataset.searchState = 'results';
  }
}

/** Tracks whether the "Price Drop Only" toggle is active: restored from URL on page load */
let _priceDropOnly = new URLSearchParams(window.location.search).get('price_drop') === '1';
/** Load More pagination state: module-level so the stats hook can read currentPage */
let _loadMoreCurrentPage = 0;
let _loadMoreTotalPages = 1;
let _loadMoreTotalHits = 0;
let _loadMoreHitsPerPage = HITS_PER_PAGE_DESKTOP;
let _emptyCollectionFallbackResolved = false;

function formatProductCountNumber(value) {
  const locale = document.documentElement.lang || window.Shopify?.locale || undefined;
  return new Intl.NumberFormat(locale).format(value);
}

function getShownProductCount() {
  if (!_loadMoreTotalHits) return 0;
  const pageCount = Math.max(_loadMoreCurrentPage + 1, 1);
  return Math.min(_loadMoreTotalHits, pageCount * _loadMoreHitsPerPage);
}

function getProductCountText() {
  const shown = getShownProductCount();
  const total = Math.max(_loadMoreTotalHits, 0);
  return T.productCount(
    formatProductCountNumber(shown),
    formatProductCountNumber(total),
  );
}

function updateShownProductCount() {
  const countEl = document.querySelector('.ais-custom-load-more-count');
  if (!countEl) return;

  countEl.textContent = getProductCountText();
  countEl.style.display = _loadMoreTotalHits > 0 ? 'block' : 'none';
}

function collectionUrlHasActiveFilters() {
  return Array.from(new URLSearchParams(window.location.search).keys()).some((key) => {
    const normalizedKey = key.toLowerCase();
    return normalizedKey !== 'page' && !normalizedKey.includes('sort');
  });
}

/**
 * Shopify's collection count can include products excluded from the Algolia
 * storefront index, so resolve the empty-collection fallback from Algolia's
 * first unfiltered result instead. Resolve only once: later zero-result states
 * caused by shopper-selected filters must not reveal the category tiles.
 * @param {number} hitCount
 */
function resolveEmptyCollectionFallback(hitCount) {
  if (_emptyCollectionFallbackResolved || isSearchPage()) return;

  const fallback = document.querySelector('[data-empty-collection-fallback]');
  if (!fallback) return;

  const isEmptyCollection = hitCount === 0 && !collectionUrlHasActiveFilters();
  fallback.hidden = !isEmptyCollection;
  document.querySelector('.algolia-collection')?.classList.toggle('algolia-collection--empty', isEmptyCollection);
  _emptyCollectionFallbackResolved = true;
}

/**
 * Show "No products found" when Algolia returns 0 hits or an error.
 * Ensures the user sees feedback instead of a stuck skeleton / empty page.
 * Safe to call multiple times: no-ops if the message already exists.
 */
function showNoResultsFallback() {
  // Don't duplicate if IS already rendered its own empty template
  if (document.querySelector('.ais-hits-empty')) return;

  const hitsContainer =
    document.querySelector('.ais-Hits') ||
    document.querySelector('.ais-hits-container') ||
    document.querySelector('.ais-block');
  if (!hitsContainer) return;

  // Check if there's already a hits list with items: results are showing, nothing to do
  const hitsList = hitsContainer.querySelector('.ais-Hits-list');
  if (hitsList && hitsList.children.length > 0) return;

  const emptyDiv = document.createElement('div');
  emptyDiv.className = 'ais-hits-empty';
  emptyDiv.textContent = T.noProductsFound();

  // Replace the hits list (which may be empty) or append
  if (hitsList) {
    hitsList.replaceWith(emptyDiv);
  } else {
    hitsContainer.appendChild(emptyDiv);
  }

  // Hide Load More control
  const loadMoreBtn = document.querySelector('.ais-custom-load-more');
  if (loadMoreBtn) loadMoreBtn.style.display = 'none';
  const loadMoreCount = document.querySelector('.ais-custom-load-more-count');
  if (loadMoreCount) loadMoreCount.style.display = 'none';
}

function registerAlgoliaHooks() {
  const isMobile = window.innerWidth < 768;
  const HITS_PER_PAGE = isMobile ? HITS_PER_PAGE_MOBILE : HITS_PER_PAGE_DESKTOP;

  // Customize stats to show product count only.
  // Also keeps Load More visibility in sync: stats fires on every IS search (filter/sort/query),
  // which always resets IS to page 0: so we reset _loadMoreCurrentPage too.
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchStatsTemplate",
    function (_defaultTemplate, data, html) {
      if (data.nbPages !== undefined) {
        _loadMoreTotalPages = data.nbPages;
        _loadMoreTotalHits = data.nbHits || 0;
        _loadMoreHitsPerPage = data.hitsPerPage || HITS_PER_PAGE;
        // If extra pages were appended by Load More, trim them: IS uses lit-html
        // diffing so it only updates the first page of items in place and leaves
        // manually-appended items untouched.
        if (_loadMoreCurrentPage > 0) {
          const hitsList = document.querySelector(".ais-Hits-list");
          if (hitsList) {
            const items = hitsList.querySelectorAll(".ais-Hits-item");
            for (let i = HITS_PER_PAGE; i < items.length; i++) {
              items[i].remove();
            }
          }
        }
        _loadMoreCurrentPage = 0;
        const loadMoreBtn = document.querySelector(".ais-custom-load-more");
        if (loadMoreBtn) {
          loadMoreBtn.style.display =
            _loadMoreCurrentPage < _loadMoreTotalPages - 1 ? "block" : "none";
        }
      }

      resolveEmptyCollectionFallback(data.nbHits);

      const viewBtn = document.querySelector('.acf-view-items-btn');
      if (viewBtn) viewBtn.textContent = T.viewItems(data.nbHits);

      const productsCountEl = document.querySelector('.js-search-tab-count--products');
      if (productsCountEl) productsCountEl.textContent = `(${data.nbHits})`;

      // Mobile product count: injected directly before .ais-block so it sits
      // above the filter/sort row on mobile without touching the existing header layout.
      const aisBlock = document.querySelector('.ais-block');
      if (aisBlock) {
        let mobileCount = document.querySelector('.ais-stats-mobile-count');
        if (!mobileCount) {
          mobileCount = document.createElement('div');
          mobileCount.className = 'ais-stats-mobile-count';
          aisBlock.insertAdjacentElement('beforebegin', mobileCount);
        }
        mobileCount.textContent = `${data.nbHits} ${T.products()}`;
      }

      syncMobileSelectIfActive();

      if (isSearchPage()) {
        _searchSecondaryNbHits = -1; // reset: new search cycle starting
        _searchProductsNbHits = data.nbHits;
        setSearchState(data.query ?? '');
      }

      // Activate deferred hash tab now that Algolia facets have rendered
      if (_pendingHashTab) {
        const pendingTab = document.querySelector(`.search-tabs__tab[data-tab="${_pendingHashTab}"]`);
        if (pendingTab) pendingTab.click();
        _pendingHashTab = null;
      }

      return html`<span class="ais-stats--nb-results">${data.nbHits} ${T.products()}</span>`;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchFiltersString",
    function (filterString) {
      let resolvedFilter = filterString;
      if (isAgeGateRestrictedSearch()) {
        resolvedFilter = appendAlgoliaFilter(resolvedFilter, AGE_GATE_PRODUCT_FILTER);
      }
      return appendAlgoliaFilter(
        resolvedFilter,
        getTemporaryLanguageFilter('products'),
      );
    },
  );

  // Patch the IS search client before IS starts so every outgoing Algolia
  // request is routed to the correct index:
  //   - Collection pages  -> the theme "Primary index" setting (algolia_primary_index_en/fr)
  //   - Search pages      -> the theme "Search index" setting (algolia_search_index_en/fr)
  //   - Prefix mismatch   -> environment prefix is remapped for all other indexes
  // IS internal state (routing, SortBy) continues to use the app's index names :
  // only the outgoing API payload is rewritten.
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchOptions",
    function (config) {
      // Suppress IS historyRouter's default window.scrollTo(0, 0) that fires
      // after every router write (including popstate-triggered rewrites).
      // IS v4 calls scrollTo synchronously inside historyRouter.write(), so this
      // temporary no-op does not leak into unrelated browser work.
      // Some Algolia app builds provide `routing: true` here and create the
      // history router later, so this guard cannot depend on config.routing.router
      // already existing. algolia-plp-hooks only loads on collection/search pages.
      installEquivalentHistoryReplace();

      const router = config.routing?.router;
      if (router && typeof router.write === 'function') {
        const _origWrite = router.write.bind(router);
        router.write = function (routeState) {
          const _origScrollTo = window.scrollTo;
          window.scrollTo = function () {};
          try {
            return _origWrite(routeState);
          } finally {
            window.scrollTo = _origScrollTo;
          }
        };
      }

      const client = config.search?.client;
      if (client && typeof client.search === "function") {
        const _orig = client.search.bind(client);
        client.search = function (requests) {
          const rewritten = (requests || []).map((req) => {
            const resolvedIndexName = resolveProductQueryIndex(req.indexName);
            return resolvedIndexName === req.indexName
              ? req
              : { ...req, indexName: resolvedIndexName };
          });
          return _orig(rewritten);
        };
      }
      return config;
    },
  );

  // Inject custom sort orders from window.algoliaCustomSort.
  // Only register hooks if the section is present: registering with no custom orders
  // interferes with Algolia's native sort rendering even when returning unchanged data.
  if (window.algoliaCustomSort?.sortOrders?.length) {
    function _buildCustomSortOrders(sortOrders) {
      const base = Array.isArray(sortOrders) ? sortOrders : (sortOrders ? [sortOrders] : []);
      const custom = window.algoliaCustomSort.sortOrders
        .filter((o) => o.value)
        .map((o) => ({ name: o.name, value: o.value }));
      return [...base, ...custom];
    }

    algoliaShopify.hooks.registerHook(
      "beforeInstantSearchInitCollectionSortOrders",
      function (sortOrders) {
        return _buildCustomSortOrders(sortOrders);
      },
    );

    algoliaShopify.hooks.registerHook(
      "beforeInstantSearchInitSearchSortOrders",
      function (sortOrders) {
        return _buildCustomSortOrders(sortOrders);
      },
    );


  }

  // ---------------------------------------------------------------
  // DOM enhancements triggered by MutationObserver
  // ---------------------------------------------------------------

  function initCustomSort(sortContainer) {
    const nativeSelect = sortContainer.querySelector(".ais-SortBy-select");
    if (!nativeSelect || sortContainer.querySelector(".ais-sort-custom"))
      return;

    // Remove "Sorted by" text node from parent .ais-sort
    const sortDiv = sortContainer.closest(".ais-sort");
    if (sortDiv) {
      sortDiv.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = "";
      });
    }

    const sortLabelMap = {};
    // English-only label map so GA4 sorted_by stays language-agnostic: both the
    // sort name and the index value are localized per session, so name_en (the
    // always-English title) is the stable analytics label. Both these map keys
    // and option.dataset.value at click time derive from the same
    // window.algoliaCustomSort.sortOrders[i].value, so the FR lookup matches by
    // construction; keep that identity if _buildCustomSortOrders changes.
    const sortLabelEnMap = {};
    const _customSortOrders = window.algoliaCustomSort?.sortOrders || [];
    _customSortOrders.forEach((o) => {
      if (o.value) {
        sortLabelMap[o.value] = o.name;
        sortLabelEnMap[o.value] = o.name_en || o.name;
      }
    });
    const _defaultSortLabel = _customSortOrders.find((o) => o.value === "")?.name;

    const translateSortLabel = (text, value) => {
      if (value && sortLabelMap[value]) return sortLabelMap[value];
      if (text === "Relevance") return _defaultSortLabel || algoliaShopify.translations?.relevance || text;
      return text;
    };
    const currentOption = nativeSelect.options[nativeSelect.selectedIndex];
    const wrapper = document.createElement("div");
    wrapper.className = "ais-sort-custom";

    wrapper.innerHTML = `
      <button type="button" class="ais-sort-custom-trigger" data-action="toggle-sort">
      <svg class="ais-sort-custom-icon" width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.56726 0.244167C3.72353 0.087941 3.93546 0.000178019 4.15643 0.000178019C4.3774 0.000178019 4.58932 0.087941 4.74559 0.244167L8.07893 3.5775C8.23073 3.73467 8.31472 3.94517 8.31282 4.16367C8.31092 4.38216 8.22328 4.59118 8.06878 4.74568C7.91427 4.90019 7.70526 4.98783 7.48676 4.98973C7.26826 4.99163 7.05776 4.90763 6.90059 4.75583L4.98976 2.845V12.5C4.98976 12.721 4.90196 12.933 4.74568 13.0893C4.5894 13.2455 4.37744 13.3333 4.15643 13.3333C3.93541 13.3333 3.72345 13.2455 3.56717 13.0893C3.41089 12.933 3.32309 12.721 3.32309 12.5V2.845L1.41226 4.75583C1.25509 4.90763 1.04459 4.99163 0.826092 4.98973C0.607595 4.98783 0.398585 4.90019 0.244078 4.74568C0.089571 4.59118 0.00193019 4.38216 3.15022e-05 4.16367C-0.00186718 3.94517 0.0821281 3.73467 0.233926 3.5775L3.56726 0.244167ZM11.6564 10.4883V0.833333C11.6564 0.61232 11.7442 0.400358 11.9005 0.244078C12.0568 0.0877975 12.2687 0 12.4898 0C12.7108 0 12.9227 0.0877975 13.079 0.244078C13.2353 0.400358 13.3231 0.61232 13.3231 0.833333V10.4883L15.2339 8.5775C15.3911 8.4257 15.6016 8.34171 15.8201 8.34361C16.0386 8.3455 16.2476 8.43314 16.4021 8.58765C16.5566 8.74216 16.6443 8.95117 16.6462 9.16967C16.6481 9.38816 16.5641 9.59866 16.4123 9.75583L13.0789 13.0892C12.9227 13.2454 12.7107 13.3332 12.4898 13.3332C12.2688 13.3332 12.0569 13.2454 11.9006 13.0892L8.56726 9.75583C8.41546 9.59866 8.33147 9.38816 8.33337 9.16967C8.33526 8.95117 8.4229 8.74216 8.57741 8.58765C8.73192 8.43314 8.94093 8.3455 9.15943 8.34361C9.37792 8.34171 9.58842 8.4257 9.74559 8.5775L11.6564 10.4883Z" fill="#2E7631"/>
      </svg>
      <div class="ais-sort-custom-labels">
          <span class="ais-sort-custom-title">${T.sort()}</span>
          <span class="ais-sort-custom-value">${translateSortLabel(currentOption?.text || "", currentOption?.value || "")}</span>
        </div>
      </button>
      <div class="ais-sort-custom-dropdown" data-sort-dropdown>
        ${Array.from(nativeSelect.options)
          .filter((opt) => {
            if (!window.algoliaCustomSort?.defaultIndex) return true;
            const _replicaValues = window.algoliaCustomSort.sortOrders
              .filter((o) => o.value)
              .map((o) => o.value);
            return _replicaValues.includes(opt.value);
          })
          .map(
            (opt) =>
              `<button type="button" class="ais-sort-custom-option ${opt.selected ? "ais-sort-custom-option--active" : ""}" data-action="select-sort" data-value="${opt.value}">${translateSortLabel(opt.text, opt.value)}</button>`,
          )
          .join("")}
      </div>
    `;

    nativeSelect.style.display = "none";
    sortContainer.appendChild(wrapper);

    const trigger = wrapper.querySelector('[data-action="toggle-sort"]');
    const dropdown = wrapper.querySelector("[data-sort-dropdown]");

    function closeDropdown() {
      wrapper.classList.remove("ais-sort-custom--open");
      document.removeEventListener("click", closeDropdown);
    }

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      if (wrapper.classList.toggle("ais-sort-custom--open")) {
        document.addEventListener("click", closeDropdown);
      }
    });

    dropdown.addEventListener("click", (e) => {
      const option = e.target.closest('[data-action="select-sort"]');
      if (!option) return;

      nativeSelect.value = option.dataset.value;
      nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));

      wrapper.querySelector(".ais-sort-custom-value").textContent =
        translateSortLabel(option.textContent, option.dataset.value);
      wrapper
        .querySelectorAll(".ais-sort-custom-option")
        .forEach((o) => o.classList.remove("ais-sort-custom-option--active"));
      option.classList.add("ais-sort-custom-option--active");
      publishPlpAnalyticsEvent('ocs:sort_filter', buildPlpInteractionPayload({
        event_source: getSortEventSource(),
        page_type: getSortPageType(),
        interaction_type: 'sort',
        sort_value: option.dataset.value || '',
        sorted_by: sortLabelEnMap[option.dataset.value] || option.dataset.value,
      }));

      closeDropdown();
    });
  }

  // Observer that watches for Algolia elements and re-applies enhancements after re-renders
  const uiObserver = new MutationObserver(() => {
    const sortContainer = document.querySelector(".ais-sort-orders-container");
    if (sortContainer?.querySelector(".ais-SortBy-select"))
      initCustomSort(sortContainer);
  });

  uiObserver.observe(document.body, { childList: true, subtree: true });

  // Customize product card template via the dedicated hook (replaces template on the
  // default hits widget: no duplicate widget).
   algoliaShopify.hooks.registerHook(
     "beforeInstantSearchProductTemplate",
     function (_defaultTemplate, hit, html, components) {
      // Queue variant fetch: requestAnimationFrame inside defers DOM query to after render
      algoliaQueueVariantFetch(hit.handle, hit.objectID);

      // Rewrite hit.url to an absolute path before rendering.
      // The Algolia CDN bundle's card-level click handler reads hit.url directly.
      // On FR locale the indexed hit.url can be a relative path ("fr/products/handle")
      // which the browser resolves against the collection pathname, producing the
      // doubled-locale URL /fr/collections/.../fr/products/... that was reported.
      // Overwriting hit.url here makes the bundle's handler navigate correctly too.
      hit.url = hitProductUrl(hit);

      const isFR = window.Shopify?.locale?.startsWith("fr");
      const frTitle = hit.meta?.translation?.["fr-CA_title"];
      const frVendor = hit.meta?.translation?.["fr-CA_vendor"];
      const title = algoliaHitTitle(hit);
      const vendor = (isFR && frVendor) || hit.vendor;
      const analytics = algoliaCardAnalyticsAttributes(hit);
      const cardBody = html`
          ${renderBadges(hit, html)}
          <div class="ais-hit--info">
            <div class="ais-hit--media">
              <a
                href="${hit.url}"
                class="ais-hit--link"
              >
                <img
                  class="ais-hit--picture"
                  src="${hitImageUrl(hit)}"
                  width="784"
                  height="781"
                  alt="${title}"
                  loading="lazy"
                />
              </a>
              ${renderOntarioGrownBadge(hit, html)}
            </div>

            <div class="ais-hit--details">
              ${vendor && html` <p class="ais-hit--vendor">${vendor}</p> `}

              <p class="ais-hit--title">
                <a
                  href="${hit.url}"
                  title="${title}"
                >
                  ${isFR && frTitle
                    ? frTitle
                    : (hit._highlightResult?.title
                        ? components.Highlight({ attribute: "title", hit })
                        : hit.title)}
                </a>
              </p>

              ${renderPlantType(hit, html)}
            </div>
          </div>

          <div class="ais-hit--actions">
            ${renderCannabinoidInfo(hit, html)}
            <div
              class="ais-hit--swatches ais-hit--swatches-loading"
              data-swatches
              data-loaded="false"
            >
              <span class="ais-hit--swatch-placeholder"></span>
            </div>

            <div class="ais-hit--pricing">
              ${renderPriceDisplay(hit, html)}
              ${!isInStoreOnly(hit) ? html`<div class="ais-hit--pricing-meta">
                ${renderSaveBadge(hit, html)}
                ${renderPricePerUnit(hit, html)}
              </div>` : ''}
            </div>

            <div class="ais-hit--cart">
              ${renderCartButton(hit, html)}
            </div>
          </div>
      `;
      return renderProductCardRoot(hit, analytics, cardBody, html);
    },
  );

  // Customize empty/no-results template
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchNoResultTemplate",
    function (_defaultTemplate, html) {
      return html`<div class="ais-hits-empty">${T.noProductsFound()}</div>`;
    },
  );

  // Hook to add custom widgets with Load More functionality
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchStartAddWidgets",
    function (widgets = []) {
      const { configure } = window.algoliaShopify.externals.widgets || {};

      if (configure) {
        // Collect all facet attributes from our custom filters so IS requests them.
        // Without this, attributes not configured in the Algolia app aren't returned.
        const acfFilters = window.algoliaCustomFilters?.filters || [];
        const customFacets = [];
        acfFilters.forEach(function (cfg) {
          if (cfg.type !== 'refinement_list' && cfg.type !== 'boolean_list' && cfg.type !== 'tag_list') return;
          // Single-attr disjunctive refinement_lists and tag_lists are declared as IS disjunctive
          // facets via getWidgetSearchParameters, not as regular facets here.
          const isNativeDisj = cfg.param_mode !== 'multi' &&
                               (cfg.type === 'refinement_list' || cfg.type === 'tag_list') &&
                               cfg.operator_type === 'disjunctive' &&
                               cfg.attribute && !cfg.attribute.includes(',');
          if (isNativeDisj) return;
          // boolean_list attrs are declared as disjunctive facets in getWidgetSearchParameters :
          // skip adding them as regular (conjunctive) facets here.
          if (cfg.type === 'boolean_list') return;
          if (cfg.bool_labels) {
            cfg.bool_labels.split('\n').forEach(function (line) {
              const idx = line.indexOf(':');
              if (idx > 0) { const a = line.slice(0, idx).trim(); if (a) customFacets.push(a); }
            });
          } else if (cfg.attribute) {
            cfg.attribute.split(',').forEach(function (a) { a = a.trim(); if (a) customFacets.push(a); });
          }
        });

        widgets.push(
          configure({
            hitsPerPage: HITS_PER_PAGE,
            maxValuesPerFacet: 1000,
            facetingAfterDistinct: FACETING_AFTER_DISTINCT,
            ...(customFacets.length ? { facets: customFacets } : {}),
          }),
        );
      }

      // Capture the InstantSearch helper so Load More can read active filters via helper.getQuery().
      // getWidgetSearchParameters injects the price drop filter into every IS search: IS rebuilds
      // search params from all widgets on each interaction (facet, sort, etc.), so direct
      // helper.addNumericRefinement calls don't survive; this hook ensures they always do.
      widgets.push({
        $$widgetType: "custom.helper-capture",
        init({ helper, instantSearchInstance }) {
          _instantSearchHelper = helper;
          window._algoliaIS = instantSearchInstance;

          // Apply custom default sort if configured via Algolia Custom Sort section,
          // but only on fresh page loads: not when the URL already has a sort.
          // Algolia's SortBy widget persists the chosen index in the URL. On refresh
          // with a sort in the URL, IS routing restores it -> helper already has the
          // user's chosen index. We only override when the index is still the base
          // (relevance) index, meaning no sort was chosen or persisted.
          const customDefault = window.algoliaCustomSort?.defaultIndex;
          const currentIndex = helper.getIndex();
          const knownReplicas = (window.algoliaCustomSort?.sortOrders || [])
            .filter(o => o.value)
            .map(o => o.value);
          const isBaseIndex = !knownReplicas.includes(currentIndex);
          if (customDefault && isBaseIndex) {
            helper.setIndex(customDefault);
          }

          _defaultSearchIndex = customDefault || helper.getIndex();
          window._algoliaHelper = helper;
          // Expose only the base collection filter set before custom filters run.
          // Language filtering is appended by algolia-custom-filters.js so an empty
          // base still falls back to the current collection instead of becoming truthy.
          window._algoliaBaseFilter = helper.state.filters || '';
          window._algoliaRuleContexts = helper.state.ruleContexts || [];

          // Listen for Algolia API errors (e.g. filtering on non-facetable attributes).
          // Without this, the skeleton stays forever because render() never fires on errors.
          helper.on('error', function (e) {
            console.error('[Algolia] Search error:', e?.error || e);
            if (typeof window._removeCardSkeleton === "function") {
              window._removeCardSkeleton();
            }
            showNoResultsFallback();
          });
        },
        render({ results }) {
          if (typeof window._removeCardSkeleton === "function") {
            window._removeCardSkeleton();
          }
          if (results && typeof window._acfOnSearchResult === "function") {
            window._acfOnSearchResult(results);
          }
          // If IS renders with 0 hits and no hits container is visible,
          // ensure "No products found" is shown (handles edge cases where
          // IS doesn't render its own empty template).
          if (results && results.nbHits === 0) {
            showNoResultsFallback();
          }
          updateShownProductCount();

          // After IS finishes rendering, re-apply toggle-based swatch filtering.
          // lit-html patches DOM in place so swatches may need re-rendering
          // with current toggle state (e.g. "Hide Out-of-Stock").
          if (window._acfActiveToggles && window._acfActiveToggles.length > 0) {
            requestAnimationFrame(function () {
              document.dispatchEvent(new CustomEvent("acf:toggles-changed"));
            });
          }
        },
        dispose() {
          _instantSearchHelper = null;
        },
        getWidgetSearchParameters(searchParameters, { uiState } = {}) {
          let params = typeof window._acfGetDisjunctiveSearchParams === 'function'
            ? window._acfGetDisjunctiveSearchParams(searchParameters, uiState)
            : searchParameters;
          if (_priceDropOnly) {
            return params.addNumericRefinement('compare_at_price', '>', 0);
          }
          return params;
        },
        getWidgetUiState(uiState, { searchParameters } = {}) {
          if (typeof window._acfGetWidgetUiState === 'function') {
            return window._acfGetWidgetUiState(uiState, searchParameters);
          }
          return uiState;
        },
      });

      // Secondary index queries (Collections, Pages, Articles) on search page
      if (isSearchPage()) {
        widgets.push({
          $$widgetType: 'custom.secondary-search',
          init() {},
          render({ helper }) {
            querySecondaryIndices(helper.state.query || '');
          },
          dispose() { _secondarySearchClient = null; },
        });
      }

      // Setup Load More button when hits container appears
      const loadMoreObserver = new MutationObserver(() => {
        const container = document.querySelector('.ais-hits-container') || document.querySelector(".ais-Hits")?.parentElement;
        if (container && !document.querySelector(".ais-custom-load-more")) {
          setupLoadMoreButton(HITS_PER_PAGE);
        }
      });
      loadMoreObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return widgets;
    },
  );


}

function setupLoadMoreButton(hitsPerPage) {
  const hitsContainer = document.querySelector(".ais-Hits")?.parentElement;
  if (!hitsContainer || document.querySelector(".ais-custom-load-more")) return;

  const countEl = document.createElement("div");
  countEl.className = "ais-custom-load-more-count";
  countEl.setAttribute("aria-live", "polite");
  countEl.textContent = getProductCountText();

  const loadMoreBtn = document.createElement("button");
  loadMoreBtn.className = "ocs-button ocs-button--outline-black ais-custom-load-more";
  loadMoreBtn.textContent = T.loadMore();

  const loadMoreWrap = document.createElement("div");
  loadMoreWrap.className = "ais-custom-load-more-wrap";
  loadMoreWrap.appendChild(countEl);
  loadMoreWrap.appendChild(loadMoreBtn);
  hitsContainer.parentElement.insertBefore(loadMoreWrap, hitsContainer.nextSibling);

  let isLoading = false;
  let searchClient = null;
  let algoliaIndex = null;

  function initAlgoliaClient() {
    try {
      const algoliasearch = window.algoliaShopify?.externals?.algoliasearch;
      const config = window.algoliaShopify?.config;

      if (!algoliasearch) {
        console.warn("[LoadMore] algoliasearch function not available");
        return false;
      }
      if (!config) {
        console.warn("[LoadMore] algoliaShopify.config not available");
        return false;
      }

      const appId = config.app_id;
      const apiKey = config.search_api_key || config.api_key;
      const prefix = resolveIndexPrefix();
      const isFR = window.Shopify?.locale?.startsWith("fr");

      if (appId && apiKey) {
        searchClient = algoliasearch(appId, apiKey);
        // Resolve the helper's current index through the same prefix and
        // primary/search mapping used by the InstantSearch request client.
        const helperIndex = _instantSearchHelper?.getIndex();
        const fallbackIndex = isSearchPage()
          ? resolveSearchReplicaIndex(prefix, isFR)
          : resolvePrimaryIndex(prefix, isFR);
        algoliaIndex = searchClient.initIndex(
          helperIndex ? resolveProductQueryIndex(helperIndex) : fallbackIndex,
        );
        return true;
      }

      return false;
    } catch (e) {
      console.error("[LoadMore] Error initializing client:", e);
      return false;
    }
  }

  function updateButtonVisibility() {
    loadMoreBtn.style.display =
      _loadMoreCurrentPage < _loadMoreTotalPages - 1 ? "block" : "none";
    updateShownProductCount();
  }

  function updateUrlPage(pageNum) {
    const url = new URL(window.location);
    if (pageNum > 1) {
      url.searchParams.set("page", pageNum);
    } else {
      url.searchParams.delete("page");
    }
    window.history.pushState({ page: pageNum }, "", url);
  }

  function renderProductCard(hit, fallbackIndex) {
    const indexedHit = Number.isInteger(fallbackIndex) && fallbackIndex > 0
      ? { ...hit, _ocsViewIndex: fallbackIndex }
      : hit;
    const li = document.createElement("li");
    li.className = "ais-Hits-item";
    const productCard = document.createElement("algolia-product-card");
    productCard.hit = indexedHit;
    li.appendChild(productCard);
    return li;
  }

  function detectPages() {
    const urlParams = new URLSearchParams(window.location.search);
    const startingPage = urlParams.get("page")
      ? parseInt(urlParams.get("page"), 10) - 1
      : 0;

    // totalPages is kept in sync by the beforeInstantSearchStatsTemplate hook.
    // Only action needed here is restoring previously-loaded pages from URL state.
    if (startingPage > 0) {
      loadPreviousPages(startingPage);
    } else {
      _loadMoreCurrentPage = 0;
      updateButtonVisibility();
    }
  }

  // Load previous pages and prepend them (for when user lands on page > 1)
  async function loadPreviousPages(targetPage) {
    if (!algoliaIndex && !initAlgoliaClient()) {
      _loadMoreCurrentPage = targetPage;
      updateButtonVisibility();
      return;
    }

    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = T.loading();

    try {
      // Wait for hits list to be rendered
      let hitsList = document.querySelector(".ais-Hits-list");
      if (!hitsList) {
        hitsList = await new Promise((resolve) => {
          const obs = new MutationObserver(() => {
            const el = document.querySelector(".ais-Hits-list");
            if (el) {
              obs.disconnect();
              resolve(el);
            }
          });
          obs.observe(document.body, { childList: true, subtree: true });
        });
      }

      if (_instantSearchHelper && searchClient) {
        algoliaIndex = searchClient.initIndex(
          resolveProductQueryIndex(_instantSearchHelper.getIndex()),
        );
      }

      const { query = "", ...baseParams } = _instantSearchHelper
        ? _instantSearchHelper.getQuery()
        : {};
      if (!_instantSearchHelper) {
        const collectionFilter = getCollectionFilter();
        if (collectionFilter) baseParams.filters = collectionFilter;
      }
      if (isAgeGateRestrictedSearch()) {
        baseParams.filters = appendAlgoliaFilter(baseParams.filters, AGE_GATE_PRODUCT_FILTER);
      }
      baseParams.filters = appendAlgoliaFilter(
        baseParams.filters,
        getTemporaryLanguageFilter('products'),
      );
      baseParams.hitsPerPage = hitsPerPage;
      baseParams.distinct = true;

      const existingIds = new Set(
        Array.from(hitsList.querySelectorAll("[data-algolia-objectid]")).map(
          (el) => el.dataset.algoliaObjectid,
        ),
      );

      const fragment = document.createDocumentFragment();

      for (let page = 0; page < targetPage; page++) {
        const results = await algoliaIndex.search(query, {
          ...baseParams,
          page,
        });
        _loadMoreTotalPages = results.nbPages;

        results.hits.forEach((hit, hitIndex) => {
          if (!existingIds.has(hit.objectID)) {
            const oneBasedIndex = page * hitsPerPage + hitIndex + 1;
            fragment.appendChild(renderProductCard(hit, oneBasedIndex));
            existingIds.add(hit.objectID);
          }
        });
      }

      hitsList.insertBefore(fragment, hitsList.firstChild);
      _loadMoreCurrentPage = targetPage;
    } catch (err) {
      _loadMoreCurrentPage = targetPage;
    }

    loadMoreBtn.disabled = false;
    loadMoreBtn.textContent = T.loadMore();
    updateButtonVisibility();
  }

  // Load more products via Algolia API
  async function loadMore() {
    if (isLoading || _loadMoreCurrentPage >= _loadMoreTotalPages - 1) return;

    if (!algoliaIndex && !initAlgoliaClient()) {
      console.error("[LoadMore] Cannot load - no Algolia client");
      return;
    }

    isLoading = true;
    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = T.loading();

    const nextPage = _loadMoreCurrentPage + 1;

    try {
      // Resolve current index dynamically: sort switches the helper's index,
      // and this manual client bypasses the InstantSearch request rewrite.
      if (_instantSearchHelper && searchClient) {
        algoliaIndex = searchClient.initIndex(
          resolveProductQueryIndex(_instantSearchHelper.getIndex()),
        );
      }

      // Use helper.getQuery() so all active facet filters, price ranges, etc. are included
      const { query = "", ...baseParams } = _instantSearchHelper
        ? _instantSearchHelper.getQuery()
        : {};
      if (!_instantSearchHelper) {
        const collectionFilter = getCollectionFilter();
        if (collectionFilter) baseParams.filters = collectionFilter;
      }
      if (isAgeGateRestrictedSearch()) {
        baseParams.filters = appendAlgoliaFilter(baseParams.filters, AGE_GATE_PRODUCT_FILTER);
      }
      baseParams.filters = appendAlgoliaFilter(
        baseParams.filters,
        getTemporaryLanguageFilter('products'),
      );
      const searchParams = {
        ...baseParams,
        page: nextPage,
        hitsPerPage,
        distinct: true,
      };
      const results = await algoliaIndex.search(query, searchParams);

      if (results.hits.length > 0) {
        const hitsList = document.querySelector(".ais-Hits-list");
        if (hitsList) {
          const existingIds = new Set(
            Array.from(
              hitsList.querySelectorAll("[data-algolia-objectid]"),
            ).map((el) => el.dataset.algoliaObjectid),
          );

          let firstNewCard = null;
          results.hits.forEach((hit, hitIndex) => {
            if (!existingIds.has(hit.objectID)) {
              const oneBasedIndex = nextPage * hitsPerPage + hitIndex + 1;
              const card = renderProductCard(hit, oneBasedIndex);
              if (!firstNewCard) firstNewCard = card;
              hitsList.appendChild(card);
            }
          });

          // Move focus to the first new card so keyboard users can access it
          if (firstNewCard) {
            const firstLink = firstNewCard.querySelector('a');
            if (firstLink) firstLink.focus({ preventScroll: false });
          }

          _loadMoreCurrentPage = nextPage;
          _loadMoreTotalPages = results.nbPages;
          updateUrlPage(_loadMoreCurrentPage + 1);
        }
      }

      loadMoreBtn.textContent = T.loadMore();
      loadMoreBtn.disabled = false;
      updateButtonVisibility();
    } catch (err) {
      console.error("[LoadMore] Error:", err);
      loadMoreBtn.textContent = T.loadMore();
      loadMoreBtn.disabled = false;
    }

    isLoading = false;
  }

  // Initialize
  initAlgoliaClient();
  detectPages();

  loadMoreBtn.addEventListener("click", () => {
    // PLP-001: load-more is not one of the three plp_interaction controls.
    loadMore();
  });
}

// =================================================================
// INITIALIZATION
// =================================================================

if (typeof algoliaShopify !== "undefined" && algoliaShopify.hooks) {
  registerAlgoliaHooks();
} else {
  document.addEventListener("algolia.hooks.initialize", () => {
    registerAlgoliaHooks();
  });
}

// Wire the card click shield regardless of whether hooks are already available.
// The shield uses a capture-phase document listener: it needs no Algolia API,
// so it is safe to call immediately at script evaluation time.
initCardClickShield();

window.algoliaCustom = window.algoliaCustom || {};

window.algoliaCustom.clearFiltersAndSort = function () {
  if (!_instantSearchHelper) return;
  _instantSearchHelper.clearRefinements();
  if (_defaultSearchIndex && _instantSearchHelper.getIndex() !== _defaultSearchIndex) {
    _instantSearchHelper.setIndex(_defaultSearchIndex);
    _resetSortUI();
  }
};

function _resetSortUI() {
  const nativeSelect = document.querySelector('.ais-SortBy-select');
  if (!nativeSelect || nativeSelect.selectedIndex === 0) return;
  nativeSelect.selectedIndex = 0;
  nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  const wrapper = document.querySelector('.ais-sort-custom');
  if (!wrapper) return;
  const defaultOption = wrapper.querySelector('.ais-sort-custom-option');
  if (!defaultOption) return;
  wrapper.querySelector('.ais-sort-custom-value').textContent = defaultOption.textContent;
  wrapper.querySelectorAll('.ais-sort-custom-option').forEach(o => o.classList.remove('ais-sort-custom-option--active'));
  defaultOption.classList.add('ais-sort-custom-option--active');
}

if (isSearchPage()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchTabs);
  } else {
    initSearchTabs();
  }
}


(function syncSearchPageInputToAlgolia() {
  const nativeInput = document.querySelector('search-page-input-component [ref="searchPageInput"]');
  if (!nativeInput) return;

  nativeInput.addEventListener('input', () => {
    window.algoliaCustom?.clearFiltersAndSort?.();
    const algoliaInput = document.querySelector('.ais-SearchBox-input');
    if (!algoliaInput) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeInputValueSetter.call(algoliaInput, nativeInput.value);
    algoliaInput.dispatchEvent(new Event('input', { bubbles: true }));
  });
})();

// When the page scrolls back to top, smoothly reset the facets panel scroll too.

(function syncFacetsScrollToTop() {
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      if (window.scrollY === 0) {
        const facets = document.querySelector('.ais-page .ais-facets');
        if (facets && facets.scrollTop > 0) {
          facets.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      ticking = false;
    });
  }, { passive: true });
})();

// =================================================================
// CARD CLICK SHIELD
// =================================================================

/**
 * Prevent the Algolia CDN bundle's `.ais-Hits-item` click handler from
 * navigating when the user clicks on empty card space.
 *
 * The CDN bundle attaches a bubbling click listener to every `.ais-Hits-item`
 * that calls `window.location.assign(hit.url)`. We cannot edit that bundle, so
 * we neutralise it by running a capture-phase listener on `document` that fires
 * *before* the bundle's handler. When the click target is not inside a genuine
 * interactive element (anchor, button, or a `[data-action]` element such as a
 * swatch or add-to-cart button), we call `e.stopPropagation()` so the CDN
 * handler never sees the event.
 *
 * Covers both render paths:
 *   - IS (lit-html) path  -> `.ais-Hits-item` wraps `.ais-hit.ais-product`
 *   - Load More path      -> `renderProductCard()` also uses `.ais-Hits-item`
 *                            wrapping an `<algolia-product-card>` element
 *
 * Allowed click targets (pass through untouched):
 *   a         : product image link, title link, "more options" link
 *   button    : swatch buttons, add-to-cart button
 *   [data-action]: belt-and-suspenders catch for any data-action element
 *                   that is not already a <button> (defensive, future-proof)
 */
function initCardClickShield() {
  document.addEventListener(
    'click',
    function cardClickShield(e) {
      // Only act when the click is inside a `.ais-Hits-item` card wrapper.
      const item = e.target.closest('.ais-Hits-item');
      if (!item) return;

      // PLP-001: a product-card click is an item view (covered by the view_item
      // origin boundary), not a plp_interaction.

      // If the click originated on (or inside) an interactive element,
      // let it propagate normally: the anchor/button handles it.
      if (e.target.closest('a, button, [data-action]')) return;

      // Otherwise this is a click on empty card space (padding, gap, image
      // container background, actions area background, etc.). Stop it from
      // reaching the CDN bundle's bubbling handler.
      e.stopPropagation();
    },
    true, // capture phase: runs before any bubbling listener, including the CDN bundle's
  );
}

// ---------------------------------------------------------------------------
// PLP title-height equalizer
// Aligns title heights per row so cards in the same visual row have
// consistent title area height, regardless of copy length.
// CSS min-height statics are removed; this function takes over that job
// dynamically and responds to Algolia re-renders and viewport resizes.
// ---------------------------------------------------------------------------

function equalizePlpTitleHeights() {
  var list = document.querySelector('.ais-Hits-list');
  if (!list) return;

  var titles = Array.from(list.querySelectorAll('.ais-hit--title'));
  if (!titles.length) return;

  // PASS 1: reset inline min-heights from previous run
  titles.forEach(function(t) { t.style.minHeight = ''; });

  // Force layout flush so measurements in PASS 2 reflect natural heights
  // eslint-disable-next-line no-unused-expressions
  list.offsetHeight;

  // PASS 2: measure and group by row using card's top position
  var rows = {};
  titles.forEach(function(t) {
    var card = t.closest('.ais-product') || t.closest('.ais-Hits-item');
    if (!card) return;
    var rowKey = Math.round(card.getBoundingClientRect().top);
    if (!rows[rowKey]) rows[rowKey] = [];
    rows[rowKey].push({ title: t, height: Math.ceil(t.getBoundingClientRect().height) });
  });

  // PASS 3: apply per-row max height
  Object.keys(rows).forEach(function(key) {
    var group = rows[key];
    var max = group.reduce(function(m, item) { return Math.max(m, item.height); }, 0);
    group.forEach(function(item) { item.title.style.minHeight = max + 'px'; });
  });
}

function equalizeMobilePlpCardSections() {
  var list = document.querySelector('.ais-Hits-list');
  if (!list) return;

  var selectors = [
    '.ais-hit--info',
    '.ais-hit--cannabinoids',
    '.ais-hit--swatches',
    '.ais-hit--pricing',
  ];
  var cards = Array.from(list.querySelectorAll('.ais-hit.ais-product'));

  cards.forEach(function(card) {
    selectors.forEach(function(selector) {
      var element = card.querySelector(selector);
      if (element) element.style.minHeight = '';
    });
  });

  if (!window.matchMedia('(max-width: 767px)').matches) return;

  // Measure only after clearing prior values, then align each semantic section
  // against the adjacent card in the same visual grid row.
  // eslint-disable-next-line no-unused-expressions
  list.offsetHeight;
  var rows = {};
  cards.forEach(function(card) {
    var rowKey = Math.round(card.getBoundingClientRect().top);
    if (!rows[rowKey]) rows[rowKey] = [];
    rows[rowKey].push(card);
  });

  Object.keys(rows).forEach(function(key) {
    selectors.forEach(function(selector) {
      var elements = rows[key]
        .map(function(card) { return card.querySelector(selector); })
        .filter(Boolean);
      var maxHeight = elements.reduce(function(max, element) {
        return Math.max(max, Math.ceil(element.getBoundingClientRect().height));
      }, 0);
      elements.forEach(function(element) { element.style.minHeight = maxHeight + 'px'; });
    });
  });
}

(function initPlpTitleEqualizer() {
  var rafId = null;
  var equalizing = false;

  function scheduleEqualize() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function() {
      rafId = null;
      // Guard: don't re-enter while we're applying inline styles
      if (equalizing) return;
      equalizing = true;
      equalizePlpTitleHeights();
      equalizeMobilePlpCardSections();
      equalizing = false;
    });
  }

  // Watch for Algolia re-renders (new cards appended on search/filter/page).
  // Use childList + subtree only: NOT attributes:true: so setting
  // min-height inline doesn't trigger this observer (avoids style-driven loop).
  var mutationObserver = new MutationObserver(function(mutations) {
    if (equalizing) return; // skip callbacks fired during our own PASS 3
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length || mutations[i].removedNodes.length) {
        scheduleEqualize();
        break;
      }
    }
  });

  // Watch for layout changes (column count changes at breakpoints).
  // The `equalizing` flag prevents the size change caused by PASS 3 from
  // triggering a second equalize run.
  var resizeObserver = new ResizeObserver(function() {
    if (equalizing) return;
    scheduleEqualize();
  });

  function attachObservers() {
    var list = document.querySelector('.ais-Hits-list');
    if (!list) return false;
    mutationObserver.observe(list, { childList: true, subtree: true });
    resizeObserver.observe(list);
    return true;
  }

  // Wait for the grid to appear (Algolia renders async after page load).
  function waitForGrid() {
    if (attachObservers()) {
      scheduleEqualize();
      return;
    }
    // Poll with rAF until the list is present
    requestAnimationFrame(waitForGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGrid);
  } else {
    waitForGrid();
  }
})();

// Track #algolia-search-bar width and expose as --algolia-search-bar-width on body.
// Used to size .aa-Panel to match the search bar exactly.